import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  View,
  StyleSheet,
  Alert,
  Platform,
  Text,
  TouchableOpacity,
  Dimensions,
  Image,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useFocusEffect } from '@react-navigation/native';
import { MapView, MapMarker } from '../components/Map';
import * as Location from 'expo-location';
import { useAuth } from '../services/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useMapReady } from '../contexts/MapReadyContext';
import { useMapProvider } from '../contexts/MapProviderContext';
import { getColors } from '../styles/theme';
import Icon from 'react-native-vector-icons/MaterialIcons';
import UserProfilePopup from '../components/UserProfilePopup';
import HotspotPopup from '../components/HotspotPopup';
import HotspotDetailModal from '../components/HotspotDetailModal';
import QuickPairSettingsModal from '../components/QuickPairSettingsModal';
import QuickPairSearchOverlay from '../components/QuickPairSearchOverlay';
import GameResultPopup from '../components/GameResultPopup';
import WelcomeProPopup from '../components/WelcomeProPopup';
import TournamentMarker from '../components/TournamentMarker';
import TournamentPopup from '../components/TournamentPopup';
import MapFilterModal from '../components/MapFilterModal';
import ClubLocationPickerModal from '../components/ClubLocationPickerModal';
import { findMatches } from '../services/matchingService';
import { useToast } from '../contexts/ToastContext';
import LoadingAnimation from '../components/LoadingAnimation';
import { listHotspots, getHotspotsInRegion, getHotspotByClaimedBy, createQuickPairRequest as createQuickPairRequestSupabase, getActiveQuickPairRequests, getQuickPairRequestById, deactivateQuickPairRequest as deactivateQuickPairRequestSupabase, updateUser as updateUserSupabase, listUsers as listUsersSupabase, createGame as createGameSupabase, findGamesBetweenPlayers, listEvents as listEventsSupabase, listTournaments as listTournamentsSupabase, getFriendIds } from '../services/database';
import { getUnreadCountFromUser } from '../services/chatService';
import { subscribeToTable, unsubscribe } from '../services/supabase';
import { isInsidePrivacyZone } from '../config/maps';
import { getFuzzedLocation, getRandomPointWithinRadius } from '../utils/locationPrivacy';
import { getProfilePictureUrl } from '../services/storage';

const { width, height } = Dimensions.get('window');
const isMobile = width < 768;

// Popup dimensions (must match UserProfilePopup / HotspotPopup) for centering math
const getPopupHeight = (viewportWidth, viewportHeight, type) => {
  const isMob = viewportWidth < 768;
  if (type === 'user') {
    if (isMob) {
      // Match hotspot: same formula so centering is identical
      const available = viewportHeight - 85 - 48;
      return Math.max(320, available);
    }
    return Math.min(480, viewportHeight * 0.65);
  }
  if (isMob) {
    const available = viewportHeight - 85 - 48;
    return Math.max(320, available);
  }
  return Math.min(500, viewportHeight * 0.6);
};
// User popup: on desktop anchor is 430px above marker; on mobile popup is above marker (same as hotspot).
const USER_POPUP_ANCHOR_Y = 430;

/**
 * Compute map center so the popup's center is at the viewport center.
 * Uses actual map container size and bounds so it works on any screen and after resize.
 */
const getMapCenterForPopupCentered = (map, markerLat, markerLng, popupType) => {
  if (!map || typeof map.getDiv !== 'function' || typeof map.getBounds !== 'function') {
    return { lat: markerLat, lng: markerLng };
  }
  const div = map.getDiv();
  if (!div || !div.offsetWidth || !div.offsetHeight) {
    return { lat: markerLat, lng: markerLng };
  }
  const viewportHeight = div.offsetHeight;
  const viewportWidth = div.offsetWidth;
  const bounds = map.getBounds();
  if (!bounds) return { lat: markerLat, lng: markerLng };
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const latSpan = ne.lat() - sw.lat();
  const lngSpan = ne.lng() - sw.lng();
  if (latSpan <= 0 || lngSpan <= 0) return { lat: markerLat, lng: markerLng };
  const degreesPerPixelY = latSpan / viewportHeight;

  const popupHeight = getPopupHeight(viewportWidth, viewportHeight, popupType);
  const isMob = viewportWidth < 768;
  let pixelOffsetY; // how many pixels below viewport center the marker should be
  if (popupType === 'user') {
    if (isMob) {
      // Mobile: user popup is now above marker (same as hotspot). Use same centering.
      const nudgeDown = 28;
      pixelOffsetY = popupHeight / 2 + nudgeDown;
    } else {
      pixelOffsetY = USER_POPUP_ANCHOR_Y - popupHeight / 2;
    }
  } else {
    // Hotspot (and tournament): popup is above marker on both desktop and mobile; center so popup is in view.
    const nudgeDown = isMob ? 28 : 0; // shift popup down a bit on mobile so it looks vertically centered
    pixelOffsetY = popupHeight / 2 + nudgeDown;
  }
  const targetLat = markerLat + pixelOffsetY * degreesPerPixelY;
  const targetLng = markerLng;
  return { lat: targetLat, lng: targetLng };
};

// Fewer markers on mobile for better map performance
const MOBILE_MAX_USER_MARKERS = 20;
const MOBILE_MAX_HOTSPOT_MARKERS = 30;
const MOBILE_MAX_TOURNAMENT_MARKERS = 8;
// Debounce map idle to prevent fetch storm when user pans/zooms rapidly
const MAP_IDLE_DEBOUNCE_MS = 400;

const MapScreen = ({ navigation, route }) => {
  const [location, setLocation] = useState(null);
  const [locationDisplay, setLocationDisplay] = useState(null); // Fuzzed "You" marker position (within 200m)
  const [nearbyUsers, setNearbyUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [region, setRegion] = useState(null);
  const [currentUserDbId, setCurrentUserDbId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserData, setSelectedUserData] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [isViewingPopup, setIsViewingPopup] = useState(false); // Flag to prevent auto-centering
  const [gameResult, setGameResult] = useState(null); // Game completion result
  const [showWelcomePro, setShowWelcomePro] = useState(false); // Welcome to Pro popup
  const [locationEnabled, setLocationEnabled] = useState(true); // Location ON by default when user joins the app
  const [showLocationOffBanner, setShowLocationOffBanner] = useState(false);
  const [locationBlockedInsecureContext, setLocationBlockedInsecureContext] = useState(false); // true when loaded over HTTP (e.g. LAN testing) so geolocation is blocked by browser
  
  // Hotspot state
  const [hotspots, setHotspots] = useState([]);
  const [selectedHotspot, setSelectedHotspot] = useState(null);
  const [showHotspotPopup, setShowHotspotPopup] = useState(false);
  const [skipCenterSyncForPopupOpen, setSkipCenterSyncForPopupOpen] = useState(false); // Prevent React center sync when opening popup (avoids overlay shift)
  const [showHotspotDetailModal, setShowHotspotDetailModal] = useState(false);
  
  // Events state - track clubs with events tonight
  const [clubsWithEventsTonight, setClubsWithEventsTonight] = useState(new Set());
  
  // Tournament state
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [showTournamentPopup, setShowTournamentPopup] = useState(false);
  
  // Map dragging state for UI fade effect
  const [isMapDragging, setIsMapDragging] = useState(false);
  const [frozenPopupCoords, setFrozenPopupCoords] = useState(null); // { x, y } viewport px when popup is open during drag (web)
  const [uiFadeEnabled, setUiFadeEnabled] = useState(true); // Default enabled
  const dragTimeoutRef = useRef(null);
  const isDraggingRef = useRef(false);
  const lastDragEndTimeRef = useRef(0);
  const pendingZoomRef = useRef(null);
  const pendingHotspotsRef = useRef(null);
  const locationOffBannerTimerRef = useRef(null);
  const usersRealtimeRef = useRef(null);
  const mapIdleDebounceRef = useRef(null);
  const popupOpeningRef = useRef(false); // true from start of pan-to-marker until popup is shown (prevents idle fetch during open)
  const popupOpenRef = useRef(false); // true when any popup is open (prevents marker fetch while viewing popup)
  // Prevent out-of-order async responses from causing marker flicker.
  const nearbyUsersRequestSeqRef = useRef(0);
  const hotspotsRequestSeqRef = useRef(0);
  
  // Zoom level for clustering
  const [currentZoom, setCurrentZoom] = useState(18); // Default to zoomed in
  
  const mapRef = useRef(null);
  const initialCenterDoneRef = useRef(false);
  const { user, appUser, updateUserProfile } = useAuth();
  const isAdmin = appUser?.isAdmin === true;
  // Approved clubs are always visible (no visibility toggle); pending clubs can use app as player
  const isApprovedClub = appUser?.accountType === 'CLUB' && appUser?.approvalStatus === 'APPROVED';
  const showLocationToggle = !isApprovedClub;
  const effectiveLocationEnabled = locationEnabled || isApprovedClub;
  const { showToast } = useToast();
  const { isDarkMode } = useTheme();
  const { showStreetLabels } = useMapProvider();
  const colors = getColors(isDarkMode);

  // Quick Pair state
  const [showQuickPairSettings, setShowQuickPairSettings] = useState(false);
  const [quickPairSearching, setQuickPairSearching] = useState(false);
  const [quickPairMatchFound, setQuickPairMatchFound] = useState(false);
  const [matchedUser, setMatchedUser] = useState(null);
  const [matchedUserRequestId, setMatchedUserRequestId] = useState(null);
  const [matchDistance, setMatchDistance] = useState(0);
  const [matchPhase, setMatchPhase] = useState(1);
  const [searchTimeRemaining, setSearchTimeRemaining] = useState(30);
  const [myQuickPairRequest, setMyQuickPairRequest] = useState(null);
  const [myRating, setMyRating] = useState(1200);
  const [myProfilePictureUrl, setMyProfilePictureUrl] = useState(null);
  const [lastQuickPairSettings, setLastQuickPairSettings] = useState(null); // Store last used settings for retry
  const searchIntervalRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const matchFoundPollingRef = useRef(null); // Monitor if opponent cancels
  
  // Filter state
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});

  // Club location picker state
  const [showClubLocationPicker, setShowClubLocationPicker] = useState(false);
  const clubLocationPromptShownRef = useRef(false);
  // Track the club's saved marker position (independent of appUser which may be stale)
  const [clubMarkerLocation, setClubMarkerLocation] = useState(null);

  // Location privacy – friend IDs (for location_visibility filtering)
  const friendIdsRef = useRef(new Set());
  // Current user's location_visibility preference ('everyone' | 'friends_only')
  const [locationVisibility, setLocationVisibility] = useState('everyone');

  // Map layer: 'all' | 'hotspots' | 'players' – quick toggle for what's visible
  const [mapLayerMode, setMapLayerMode] = useState('all');
  // When switching layer, the "hidden" layer plays exit animation before unmount (e.g. 'players' | 'hotspots')
  const [exitingLayer, setExitingLayer] = useState(null);
  const prevMapLayerModeRef = useRef(mapLayerMode);
  const exitTimeoutRef = useRef(null);

  // When layer mode changes, set exiting layer and clear after animation duration
  useEffect(() => {
    if (prevMapLayerModeRef.current === mapLayerMode) return;
    const prev = prevMapLayerModeRef.current;
    prevMapLayerModeRef.current = mapLayerMode;
    if (exitTimeoutRef.current) clearTimeout(exitTimeoutRef.current);
    if (mapLayerMode === 'hotspots') {
      setExitingLayer('players');
    } else if (mapLayerMode === 'players') {
      setExitingLayer('hotspots');
    } else {
      setExitingLayer(null);
      return;
    }
    exitTimeoutRef.current = setTimeout(() => {
      setExitingLayer(null);
      exitTimeoutRef.current = null;
    }, 350);
    return () => {
      if (exitTimeoutRef.current) clearTimeout(exitTimeoutRef.current);
    };
  }, [mapLayerMode]);

  // Fetch friend IDs for location_visibility filtering (once on load / when user changes)
  useEffect(() => {
    if (!currentUserDbId) return;
    getFriendIds(currentUserDbId)
      .then((ids) => {
        friendIdsRef.current = new Set(ids);
        console.log(`👫 Loaded ${ids.length} friend IDs for location visibility`);
      })
      .catch((err) => {
        console.error('Error fetching friend IDs:', err);
      });
  }, [currentUserDbId]);

  // Sync locationVisibility from the user profile
  useEffect(() => {
    if (appUser?.location_visibility) {
      setLocationVisibility(appUser.location_visibility);
    }
  }, [appUser?.location_visibility]);

  useEffect(() => {
    // Safety timeout - ensure loading is set to false after 10 seconds max
    const loadingTimeout = setTimeout(() => {
      console.warn('⚠️ Loading timeout - forcing loading to false');
      setLoading(false);
    }, 10000);

    // Always request location (app always knows where user is)
    requestLocationPermission().catch((error) => {
      console.error('Error in requestLocationPermission:', error);
      setLoading(false);
    });
    
    // Use appUser.id directly (Supabase database ID) instead of fetching via GraphQL
    if (appUser?.id) {
      setCurrentUserDbId(appUser.id);
      console.log('✅ Current user DB ID:', appUser.id);
    } else {
      fetchCurrentUserDbId().catch((error) => {
        console.error('Error fetching user DB ID:', error);
      });
    }
    
    // Load UI fade preference
    const loadUiFadePreference = async () => {
      try {
        let value = null;
        if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
          value = localStorage.getItem('uiFadeEnabled');
        } else {
          value = await AsyncStorage.getItem('uiFadeEnabled');
        }
        if (value !== null) {
          setUiFadeEnabled(JSON.parse(value));
        }
      } catch (error) {
        console.error('Error loading UI fade preference:', error);
      }
    };
    
    loadUiFadePreference();
    
    // Cleanup function to clear all intervals when component unmounts
    return () => {
      clearTimeout(loadingTimeout);
      console.log('🧹 MapScreen unmounting - cleaning up Quick Pair intervals');
      clearInterval(searchIntervalRef.current);
      clearInterval(timerIntervalRef.current);
      clearInterval(matchFoundPollingRef.current);
      // Also cleanup drag timeout
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }
    };
  }, []);

  // Update currentUserDbId when appUser changes
  useEffect(() => {
    if (appUser?.id && appUser.id !== currentUserDbId) {
      setCurrentUserDbId(appUser.id);
      console.log('✅ Updated current user DB ID from appUser:', appUser.id);
    }
  }, [appUser?.id]);

  // Lock body scroll on mobile web when any popup is open so Chrome's UI doesn't cause page scroll / map glitch
  const popupOpenOnMobileWeb = Platform.OS === 'web' && isMobile && (showHotspotPopup || showPopup || showTournamentPopup);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (popupOpenOnMobileWeb) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev || ''; };
    }
  }, [popupOpenOnMobileWeb]);

  // Keep ref in sync so map-idle handler can skip marker fetch while any popup is open (avoids overlay shift from setHotspots re-render)
  useEffect(() => {
    popupOpenRef.current = !!(showHotspotPopup || showPopup || showTournamentPopup);
  }, [showHotspotPopup, showPopup, showTournamentPopup]);

  // When location updates, pick a single fuzzed position within 200m for the "You" marker (any zoom level)
  useEffect(() => {
    if (location?.latitude != null && location?.longitude != null) {
      setLocationDisplay(getRandomPointWithinRadius(location.latitude, location.longitude, 200));
    } else {
      setLocationDisplay(null);
    }
  }, [location?.latitude, location?.longitude]);

  useEffect(() => {
    if (location && currentUserDbId) {
      // Only update location in DB if sharing is enabled (approved clubs are always "on")
      if (effectiveLocationEnabled) {
        updateUserLocation();
      }
      // Only fetch data if user is authenticated
      if (appUser) {
        if (effectiveLocationEnabled) {
          fetchNearbyUsers();
        } else {
          setNearbyUsers([]);
        }
        // Use region-derived bounds for initial hotspot fetch (lighter than full list when zoomed in)
        const r = region || (location && { latitude: location.latitude, longitude: location.longitude, latitudeDelta: 0.01, longitudeDelta: 0.01 });
        if (r) {
          const pad = 0.15;
          const latD = r.latitudeDelta || 0.01;
          const lngD = r.longitudeDelta || 0.01;
          fetchHotspots({
            minLat: r.latitude - latD * (0.5 + pad),
            maxLat: r.latitude + latD * (0.5 + pad),
            minLng: r.longitude - lngD * (0.5 + pad),
            maxLng: r.longitude + lngD * (0.5 + pad),
          });
        } else {
          fetchHotspots();
        }
        fetchTodaysEvents();
        fetchActiveTournaments();
      }
    }
  }, [location, currentUserDbId, locationEnabled, effectiveLocationEnabled, appUser]);

  // Periodically update location and refresh nearby users (every 30 seconds)
  useEffect(() => {
    if (!location || !currentUserDbId || !appUser || !effectiveLocationEnabled) {
      return;
    }

    const interval = setInterval(() => {
      updateUserLocation();
      // Skip fetch during drag to avoid marker re-render ghost
      if (!isDraggingRef.current) {
        fetchNearbyUsers();
      }
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, [location, currentUserDbId, appUser, effectiveLocationEnabled]);

  // Refetch today's events every 2 min so club ripple disappears soon after an event ends
  useEffect(() => {
    if (!appUser) return;
    const interval = setInterval(() => {
      fetchTodaysEvents();
    }, 120000);
    return () => clearInterval(interval);
  }, [appUser]);

  useEffect(() => {
    if (!showLocationOffBanner) {
      return;
    }

    if (locationOffBannerTimerRef.current) {
      clearTimeout(locationOffBannerTimerRef.current);
    }

    locationOffBannerTimerRef.current = setTimeout(() => {
      setShowLocationOffBanner(false);
      locationOffBannerTimerRef.current = null;
    }, 5000);

    return () => {
      if (locationOffBannerTimerRef.current) {
        clearTimeout(locationOffBannerTimerRef.current);
        locationOffBannerTimerRef.current = null;
      }
    };
  }, [showLocationOffBanner]);

  useEffect(() => {
    if (!effectiveLocationEnabled || !location || !currentUserDbId || !appUser) {
      return;
    }

    const channel = subscribeToTable('users', (payload) => {
      const record = payload?.new_record || payload?.new || payload?.record;
      if (!record?.id) {
        return;
      }
      if (record.id === currentUserDbId) {
        return;
      }
      if (record.is_online === false || record.latitude == null || record.longitude == null) {
        setNearbyUsers((prev) => prev.filter((user) => user.id !== record.id));
      }
    });

    usersRealtimeRef.current = channel;

    return () => {
      if (usersRealtimeRef.current) {
        unsubscribe(usersRealtimeRef.current);
        usersRealtimeRef.current = null;
      }
    };
  }, [effectiveLocationEnabled, location, currentUserDbId, appUser]);

  // Handle game result from navigation params
  useEffect(() => {
    if (route?.params?.gameResult) {
      console.log('🎉 Game result received:', route.params.gameResult);
      setGameResult(route.params.gameResult);
      // Clear the param to prevent showing popup again
      navigation.setParams({ gameResult: null });
      
      // CLEANUP: Ensure all Quick Pair states are reset when returning from a game
      resetQuickPairStates();
    }
  }, [route?.params?.gameResult]);

  // Handle welcome to Pro popup from navigation params
  useEffect(() => {
    if (route?.params?.showWelcomePro) {
      console.log('🎉 Welcome to Pro flag received');
      setShowWelcomePro(true);
      // Clear the param to prevent showing popup again
      navigation.setParams({ showWelcomePro: false });
    }
  }, [route?.params?.showWelcomePro]);

  // Initialize clubMarkerLocation from appUser when available
  useEffect(() => {
    if (appUser?.accountType === 'CLUB' && appUser?.latitude && appUser?.longitude && !clubMarkerLocation) {
      setClubMarkerLocation({ latitude: appUser.latitude, longitude: appUser.longitude });
    }
  }, [appUser?.latitude, appUser?.longitude, appUser?.accountType]);

  useEffect(() => {
    if (
      appUser?.accountType !== 'CLUB' ||
      !currentUserDbId ||
      !location ||
      loading ||
      clubLocationPromptShownRef.current
    ) {
      return;
    }

    // Only prompt if the club does not yet have a marker location saved in the backend
    if (appUser?.latitude != null && appUser?.longitude != null) {
      return;
    }

    clubLocationPromptShownRef.current = true;

    let timerId = null;

    // If the club is approved, show a congratulations toast before opening the picker
    if (appUser?.approvalStatus === 'APPROVED') {
      showToast('Your club has been approved! Set your marker location on the map.', 'success');
    }

    timerId = setTimeout(() => setShowClubLocationPicker(true), 800);

    return () => {
      if (timerId) clearTimeout(timerId);
    };
  }, [appUser?.accountType, appUser?.latitude, appUser?.longitude, appUser?.approvalStatus, currentUserDbId, location, loading]);

  // Re-open location picker when navigated from Profile with openLocationPicker param
  useEffect(() => {
    if (route?.params?.openLocationPicker && appUser?.accountType === 'CLUB') {
      setShowClubLocationPicker(true);
      // Clear the param so it doesn't re-trigger
      navigation.setParams({ openLocationPicker: undefined });
    }
  }, [route?.params?.openLocationPicker]);

  // Handler: club owner confirms their pin location
  const handleClubLocationConfirm = async ({ latitude, longitude }) => {
    try {
      await updateUserSupabase(currentUserDbId, {
        latitude,
        longitude,
        last_location_update: new Date().toISOString(),
        is_online: true,
        last_online_at: new Date().toISOString(),
      });

      // Keep local marker state and appUser in sync
      setClubMarkerLocation({ latitude, longitude });
      if (updateUserProfile) {
        updateUserProfile({ latitude, longitude }).catch(() => {});
      }

      console.log('✅ Club location pinned at:', latitude, longitude);
      showToast('Club location saved!', 'success');
      setShowClubLocationPicker(false);

      // Refresh nearby users so the club shows up
      fetchNearbyUsers();
    } catch (error) {
      console.error('❌ Error saving club location:', error);
      showToast('Failed to save location. Please try again.', 'error');
    }
  };

  // Refresh hotspots and preferences when screen comes into focus
  useFocusEffect(
    React.useCallback(() => {
    if (location) {
        console.log('🔄 MapScreen focused - refreshing hotspots...');
        fetchHotspots();
      }
      
      // Reload UI fade preference in case it was changed in settings
      const reloadUiFadePreference = async () => {
        try {
          let value = null;
          if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
            value = localStorage.getItem('uiFadeEnabled');
          } else {
            value = await AsyncStorage.getItem('uiFadeEnabled');
          }
          if (value !== null) {
            setUiFadeEnabled(JSON.parse(value));
          }
        } catch (error) {
          console.error('Error reloading UI fade preference:', error);
        }
      };
      
      reloadUiFadePreference();
    }, [location])
  );

  // Re-check for events when nearbyUsers changes (needed for demo mode with club ripples)
  useEffect(() => {
    if (nearbyUsers.length > 0 && clubsWithEventsTonight.size === 0) {
      fetchTodaysEvents();
    }
  }, [nearbyUsers]);

  // When current user is an approved club, ensure we've run fetchTodaysEvents so event ripples are up to date
  useEffect(() => {
    if (currentUserDbId && appUser?.accountType === 'CLUB' && appUser?.approvalStatus === 'APPROVED') {
      fetchTodaysEvents();
    }
  }, [currentUserDbId, appUser?.accountType, appUser?.approvalStatus]);

  const fetchCurrentUserDbId = async () => {
    // Use appUser from Clerk/Supabase auth context instead of Cognito GraphQL
    if (appUser?.id) {
      setCurrentUserDbId(appUser.id);
      console.log('✅ Current user DB ID:', appUser.id);
    }
  };

  const requestLocationPermission = async () => {
    try {
      // On web, browsers only allow geolocation in secure contexts (HTTPS or localhost).
      // When testing via LAN (e.g. http://192.168.x.x:19006), geolocation is blocked.
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        const isLocalhost = /^localhost$|^127\.0\.0\.1$/i.test(window.location.hostname);
        const isSecure = window.location.protocol === 'https:' || isLocalhost;
        if (!isSecure) {
          setLocationBlockedInsecureContext(true);
          setLocation({ latitude: 43.6532, longitude: -79.3832 });
          setRegion({
            latitude: 43.6532,
            longitude: -79.3832,
            latitudeDelta: 0.05,
            longitudeDelta: 0.05,
          });
          setLoading(false);
          return;
        }
      }

      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert(
          'Permission Denied',
          'Location permission is required to show your position on the map and find nearby players.'
        );
        setLoading(false);
        return;
      }

      await getCurrentLocation();
    } catch (error) {
      console.error('Error requesting location permission:', error);
      setLoading(false);
      // Set a default location so the map can still render
      setLocation({ latitude: 43.6532, longitude: -79.3832 }); // Toronto default
      setRegion({
        latitude: 43.6532,
        longitude: -79.3832,
        latitudeDelta: 0.05,
        longitudeDelta: 0.05,
      });
    }
  };

  const getCurrentLocation = async () => {
    try {
      setLoading(true);
      const currentLocation = await Location.getCurrentPositionAsync({
        accuracy: Location.Accuracy.High,
      });

      const { latitude, longitude } = currentLocation.coords;
      setLocation({ latitude, longitude });

      // Only auto-center if not viewing a popup
      if (!isViewingPopup) {
        // Set initial region (wider view to see nearby markers)
        const newRegion = {
          latitude,
          longitude,
          latitudeDelta: 0.005,  // Zoomed in closer
          longitudeDelta: 0.005, // Zoomed in closer
        };
        setRegion(newRegion);

        // Animate map to user location
        if (mapRef.current) {
          // For web Google Maps, use panTo for smooth animation
          try {
            mapRef.current.panTo({
              lat: newRegion.latitude,
              lng: newRegion.longitude
            });
          } catch (mapError) {
            console.error('Error animating map:', mapError);
            // Continue even if map animation fails
          }
        }
      }

    } catch (error) {
      console.error('Error getting location:', error);
      // Don't show alert if it's just a permission issue (already handled)
      if (!error.message?.includes('permission')) {
        Alert.alert('Error', 'Failed to get current location');
      }
    } finally {
      // Always set loading to false, even if there's an error
      setLoading(false);
    }
  };

  const { setMapReady } = useMapReady();

  // When map becomes ready, center on user (simulates "Find My Location" on load)
  const handleMapReady = useCallback(() => {
    setMapReady(true);
    if (initialCenterDoneRef.current) return;
    initialCenterDoneRef.current = true;

    if (location?.latitude != null && location?.longitude != null && !isViewingPopup) {
      const newRegion = {
        latitude: location.latitude,
        longitude: location.longitude,
        latitudeDelta: 0.005,
        longitudeDelta: 0.005,
      };
      setRegion(newRegion);
      try {
        if (mapRef.current?.panTo) {
          mapRef.current.panTo({ lat: newRegion.latitude, lng: newRegion.longitude });
        }
      } catch (e) {
        // ignore
      }
    } else {
      getCurrentLocation();
    }
  }, [location?.latitude, location?.longitude, isViewingPopup]);

  const updateUserLocation = async () => {
    if (!location || !currentUserDbId || !appUser) {
      return;
    }

    // Club accounts should NOT have their marker move with device GPS.
    // Their marker position is set manually via the location picker.
    if (appUser?.accountType === 'CLUB') {
      // Only update online status, never overwrite lat/lng with device location
      try {
        await updateUserSupabase(currentUserDbId, {
          is_online: true,
          last_online_at: new Date().toISOString(),
        });
      } catch (error) {
        console.error('❌ Error updating club online status:', error);
      }
      return;
    }

    try {
      // Update location and online status using Supabase
      await updateUserSupabase(currentUserDbId, {
        latitude: location.latitude,
        longitude: location.longitude,
        last_location_update: new Date().toISOString(),
        is_online: true,
        last_online_at: new Date().toISOString(),
      });
      console.log('✅ Location updated in Supabase');
    } catch (error) {
      console.error('❌ Error updating location:', error);
    }
  };

  const fetchNearbyUsers = async () => {
    if (!location) {
      return;
    }

    if (!effectiveLocationEnabled) {
      return;
    }

    // Don't fetch if user is not authenticated
    if (!appUser || !currentUserDbId) {
      return;
    }

    const requestSeq = ++nearbyUsersRequestSeqRef.current;

    try {
      // Generous fetch radius – the user-facing radius filter is applied client-side
      const radiusKm = 200;

      // Limit fetch for map performance - mobile gets fewer, cap all
      const userLimit = isMobile ? 80 : 150;
      const allUsers = await listUsersSupabase({ isOnline: true, limit: userLimit });
      if (requestSeq !== nearbyUsersRequestSeqRef.current) {
        return;
      }
      
      if (!allUsers || !Array.isArray(allUsers)) {
        console.error('❌ Invalid response from listUsers:', allUsers);
        return;
      }
      
      // Filter out current user and users without location, then filter by distance
      const nearby = allUsers.filter((u) => {
        // Skip current user — but CLUB accounts should see their own marker
        // (player accounts already have the blue GPS dot to represent themselves)
        if (currentUserDbId && u.id === currentUserDbId) {
          if (appUser?.accountType !== 'CLUB') return false;
        }
        
        // Skip users who are not online (location sharing disabled)
        if (!u.is_online) {
          console.log(`🙈 Filtering out ${u.username || u.club_name || u.id} - location sharing disabled (is_online: false)`);
          return false;
        }

        // Skip users whose location_visibility is "friends_only" if viewer is not their friend
        if (u.location_visibility === 'friends_only' && u.id !== currentUserDbId) {
          if (!friendIdsRef.current.has(u.id)) {
            console.log(`🔒 Filtering out ${u.username || u.club_name || u.id} - friends-only visibility`);
            return false;
          }
        }
        
        // Skip users without location
        if (!u.latitude || !u.longitude) {
          console.log(`🙈 Filtering out ${u.username || u.club_name || u.id} - no location data`);
          return false;
        }
        
        // Calculate distance in km using Haversine formula
        const R = 6371; // Earth's radius in km
        const dLat = (u.latitude - location.latitude) * Math.PI / 180;
        const dLon = (u.longitude - location.longitude) * Math.PI / 180;
        const a = 
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos(location.latitude * Math.PI / 180) * Math.cos(u.latitude * Math.PI / 180) *
          Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distanceKm = R * c;
        
        const withinRadius = distanceKm <= radiusKm;
        if (!withinRadius) {
          console.log(`🙈 Filtering out ${u.username || u.club_name || u.id} - outside radius (${distanceKm.toFixed(1)}km away)`);
        }
        
        return withinRadius;
      });
      
      // Convert snake_case to camelCase; defer unread counts on mobile (N+1 API calls) for performance
      const nearbyCamelCase = await Promise.all(
        nearby.map(async (u) => {
          let unreadCount = 0;
          if (!isMobile) {
            try {
              unreadCount = await getUnreadCountFromUser(currentUserDbId, u.id);
            } catch (error) {
              console.error(`Error fetching unread count for user ${u.id}:`, error);
            }
          }

          return {
            id: u.id,
            username: u.username,
            clubName: u.club_name,
            accountType: u.account_type,
            approvalStatus: u.approval_status,
            latitude: u.latitude,
            longitude: u.longitude,
            rating: u.rating || u.in_person_rating || 1200,
            profilePictureUrl: u.profile_picture_url,
            city: u.city,
            statusMessage: u.status_message,
            isOnline: u.is_online,
            lastOnlineAt: u.last_online_at,
            schedule: u.schedule,
            tier: u.tier,
            about: u.about,
            address: u.address,
            unreadCount,
          };
        })
      );
      if (requestSeq !== nearbyUsersRequestSeqRef.current) {
        return;
      }
      
      console.log(`📍 Found ${nearbyCamelCase.length} nearby users (from ${allUsers.length} total online users)`);
      setNearbyUsers(nearbyCamelCase);
    } catch (error) {
      console.error('❌ Error fetching nearby users:', error);
    }
  };

  // Calculate distance between two coordinates in meters
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  };

  const fetchHotspots = async (bounds = null) => {
    const requestSeq = ++hotspotsRequestSeqRef.current;
    try {
      let fetchedHotspots;
      if (bounds) {
        // Viewport-based fetch: only load hotspots in visible area for better performance
        fetchedHotspots = await getHotspotsInRegion(bounds, 'PUBLISHED');
      } else {
        fetchedHotspots = await listHotspots();
      }
      if (requestSeq !== hotspotsRequestSeqRef.current) {
        return;
      }
      const data = fetchedHotspots || [];
      if (isDraggingRef.current) {
        pendingHotspotsRef.current = data;
      } else {
        setHotspots(data);
      }
    } catch (error) {
      if (error?.message?.includes('No credentials')) {
        return;
      }
      console.error('Error fetching hotspots:', error);
    }
  };

  // Fetch today's events to identify clubs with events tonight
  const fetchTodaysEvents = async () => {
    // Don't fetch if user is not authenticated
    if (!appUser) {
      return;
    }
    
    try {
      // Get today's date range (start of day to end of day)
      const today = new Date();
      const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
      const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
      
      const allEvents = await listEventsSupabase({ limit: 100 });
      
      const now = new Date();
      // Filter for events happening today that haven't ended yet (so ripple disappears after event ends)
      const todaysEvents = (allEvents || []).filter(event => {
        const eventDate = new Date(event.event_date);
        if (eventDate < startOfDay || eventDate > endOfDay) return false;
        const endTime = event.end_time
          ? new Date(event.end_time)
          : new Date(eventDate.getTime() + 4 * 3600000);
        return endTime > now; // event still in progress or not yet started
      });
      
      // Extract unique club IDs that have events today (and not yet ended)
      const clubIds = new Set(todaysEvents.map(event => event.club_id));
      
      // DEMO MODE: If no real events today, randomly mark some clubs as having events
      // This is for demonstration purposes until real events are seeded
      if (clubIds.size === 0 && nearbyUsers.length > 0) {
        const clubs = nearbyUsers.filter(u => u.accountType === 'CLUB' && u.approvalStatus === 'APPROVED');
        if (clubs.length > 0) {
          clubs.forEach(club => {
            // Use club ID hash for consistent randomization
            let hash = 0;
            for (let i = 0; i < club.id.length; i++) {
              hash = ((hash << 5) - hash) + club.id.charCodeAt(i);
            }
            if (Math.abs(hash) % 3 === 0) { // ~33% of clubs
              clubIds.add(club.id);
            }
          });
          console.log('🎭 DEMO MODE: Showing ripples on', clubIds.size, 'clubs (no real events found)');
        }
      }

      setClubsWithEventsTonight(clubIds);
    } catch (error) {
      console.error('Error fetching today\'s events:', error);
    }
  };

  // Fetch active tournaments (REGISTRATION or IN_PROGRESS) to show on map
  const fetchActiveTournaments = async () => {
    // Don't fetch if user is not authenticated
    if (!appUser) {
      return;
    }
    
    try {
      const allTournaments = await listTournamentsSupabase({ limit: 50 });
      
      // Filter for active tournaments (registration open or in progress)
      const activeTournaments = (allTournaments || []).filter(
        t => t.status === 'REGISTRATION' || t.status === 'IN_PROGRESS'
      );
      
      // Filter for tournaments with valid club location
      const tournamentsWithLocation = activeTournaments.filter(
        t => t.club?.latitude && t.club?.longitude
      );
      
      // Map to camelCase for compatibility with existing components
      const mapped = tournamentsWithLocation.map(t => ({
        ...t,
        clubId: t.club_id,
        totalRounds: t.total_rounds,
        currentRound: t.current_round,
        isPaid: t.is_paid,
        entryFee: t.entry_fee,
        club: t.club ? {
          ...t.club,
          clubName: t.club.club_name,
        } : null,
        participants: { items: [] }, // count is available via t.participants
      }));
      
      console.log('🏆 Active tournaments:', mapped.length);
      mapped.forEach(t => {
        console.log(`   🏅 "${t.name}" (${t.status})`);
      });
      
      setTournaments(mapped);
    } catch (error) {
      console.error('Error fetching tournaments:', error);
    }
  };

  // Get popularity level for a hotspot (1, 2, or 3)
  // Currently using seeded random based on hotspot ID since no real popularity data exists
  // In the future, this could use: review count, activity level, player visits, etc.
  const getHotspotPopularityLevel = (hotspot) => {
    // Seeded random based on hotspot ID for consistent results
    // This creates a hash from the ID string
    let hash = 0;
    const id = hotspot.id || '';
    for (let i = 0; i < id.length; i++) {
      const char = id.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    // Convert hash to level 1, 2, or 3
    const level = (Math.abs(hash) % 3) + 1;
    return level;
  };

  // Get scale multiplier based on popularity level
  // Level 1: 1.0x (base size), Level 2: 1.075x, Level 3: 1.15x (reduced for smaller markers)
  const getHotspotScale = (hotspot) => {
    const level = getHotspotPopularityLevel(hotspot);
    const scales = {
      1: 1.0,     // Least popular - base size
      2: 1.075,   // Medium popularity - 1.075x (reduced from 1.125x)
      3: 1.15,    // Most popular - 1.15x (reduced from 1.25x)
    };
    return scales[level] || 1.0;
  };

  // Get activity level for color variety on hotspot markers
  // Uses hotspot.activityLevel if available, otherwise seeded random for variety
  const getHotspotActivityLevel = (hotspot) => {
    // Use actual activity level if available from database
    if (hotspot.activityLevel || hotspot.activity_level) {
      const level = (hotspot.activityLevel || hotspot.activity_level).toUpperCase();
      if (['HIGH', 'MEDIUM', 'LOW'].includes(level)) {
        return level;
      }
    }
    
    // Seeded random based on hotspot ID for consistent variety
    let hash = 0;
    const id = hotspot.id || '';
    for (let i = 0; i < id.length; i++) {
      const char = id.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    
    // Use different hash range than popularity to get different distribution
    const activityIndex = Math.abs(hash >> 4) % 3;
    const levels = ['LOW', 'MEDIUM', 'HIGH'];
    return levels[activityIndex];
  };

  // Toronto center coordinates for city-level marker
  const TORONTO_CENTER = { latitude: 43.6532, longitude: -79.3832 };

  // Clustering thresholds based on zoom level
  // zoom >= 10: show all individual markers
  // zoom 6-9: show clusters
  // zoom < 6: show single Toronto marker
  const ZOOM_INDIVIDUAL = 10;
  const ZOOM_CLUSTER = 6;

  // Cluster hotspots based on grid cells at a given zoom level
  const clusterHotspots = (hotspotsToCluster, zoom) => {
    if (!hotspotsToCluster || hotspotsToCluster.length === 0) return [];
    
    // Grid cell size based on zoom (larger cells at lower zoom)
    // At zoom 8, ~1 degree cells; at zoom 6, ~4 degree cells
    const cellSize = Math.pow(2, 10 - zoom) * 0.05;
    
    const clusters = {};
    
    hotspotsToCluster.forEach(hotspot => {
      if (!hotspot.latitude || !hotspot.longitude) return;
      
      // Calculate grid cell
      const cellX = Math.floor(hotspot.longitude / cellSize);
      const cellY = Math.floor(hotspot.latitude / cellSize);
      const cellKey = `${cellX}_${cellY}`;
      
      if (!clusters[cellKey]) {
        clusters[cellKey] = {
          hotspots: [],
          centerLat: 0,
          centerLng: 0,
        };
      }
      
      clusters[cellKey].hotspots.push(hotspot);
    });
    
    // Calculate cluster centers and return cluster data
    return Object.values(clusters).map(cluster => {
      const count = cluster.hotspots.length;
      const centerLat = cluster.hotspots.reduce((sum, h) => sum + h.latitude, 0) / count;
      const centerLng = cluster.hotspots.reduce((sum, h) => sum + h.longitude, 0) / count;
      
      return {
        id: `cluster-${centerLat.toFixed(4)}-${centerLng.toFixed(4)}`,
        latitude: centerLat,
        longitude: centerLng,
        count: count,
        hotspots: cluster.hotspots,
        // Use the first hotspot's category for the icon
        category: cluster.hotspots[0].category,
      };
    });
  };

  // Get display coordinates for a user (for clustering) – same logic as renderUserMarker
  const getDisplayCoordsForUser = (userData) => {
    if (!userData?.latitude || !userData?.longitude) return null;
    if (userData.accountType === 'CLUB') {
      const idHash = userData.id.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
      return {
        latitude: userData.latitude + (idHash % 100) * 0.00001,
        longitude: userData.longitude + ((idHash * 7) % 100) * 0.00001,
      };
    }
    const fuzzed = getFuzzedLocation(userData.latitude, userData.longitude, userData.id);
    return { latitude: fuzzed.latitude, longitude: fuzzed.longitude };
  };

  // Cluster users (players or clubs) using same grid logic as hotspots
  const clusterUsers = (usersToCluster, zoom) => {
    if (!usersToCluster || usersToCluster.length === 0) return [];
    const cellSize = Math.pow(2, 10 - zoom) * 0.05;
    const clusters = {};
    usersToCluster.forEach((user) => {
      const coords = getDisplayCoordsForUser(user);
      if (!coords) return;
      const cellX = Math.floor(coords.longitude / cellSize);
      const cellY = Math.floor(coords.latitude / cellSize);
      const cellKey = `${cellX}_${cellY}`;
      if (!clusters[cellKey]) {
        clusters[cellKey] = { users: [], centerLat: 0, centerLng: 0 };
      }
      clusters[cellKey].users.push(user);
    });
    return Object.values(clusters).map((cluster) => {
      const count = cluster.users.length;
      const centerLat = cluster.users.reduce((sum, u) => {
        const c = getDisplayCoordsForUser(u);
        return sum + (c ? c.latitude : 0);
      }, 0) / count;
      const centerLng = cluster.users.reduce((sum, u) => {
        const c = getDisplayCoordsForUser(u);
        return sum + (c ? c.longitude : 0);
      }, 0) / count;
      return {
        id: `user-cluster-${centerLat.toFixed(4)}-${centerLng.toFixed(4)}`,
        latitude: centerLat,
        longitude: centerLng,
        count,
        users: cluster.users,
      };
    });
  };

  // Process players (non-club users) by zoom: individual, clustered, or hidden at city level
  const getProcessedPlayers = () => {
    const players = (filteredUsers || []).filter((u) => u.accountType !== 'CLUB');
    const withCoords = players.filter((u) => u.latitude && u.longitude && !isInsidePrivacyZone(u.latitude, u.longitude));
    if (currentZoom >= ZOOM_INDIVIDUAL) {
      return { mode: 'individual', data: withCoords };
    }
    if (currentZoom >= ZOOM_CLUSTER) {
      return { mode: 'clustered', data: clusterUsers(withCoords, currentZoom) };
    }
    return { mode: 'hidden', data: [] };
  };

  // Process clubs by zoom: individual, clustered, or hidden at city level
  const getProcessedClubs = () => {
    const clubs = (filteredUsers || []).filter((u) => u.accountType === 'CLUB');
    const withCoords = clubs.filter((u) => u.latitude && u.longitude && !isInsidePrivacyZone(u.latitude, u.longitude));
    if (currentZoom >= ZOOM_INDIVIDUAL) {
      return { mode: 'individual', data: withCoords };
    }
    if (currentZoom >= ZOOM_CLUSTER) {
      return { mode: 'clustered', data: clusterUsers(withCoords, currentZoom) };
    }
    return { mode: 'hidden', data: [] };
  };

  // Get processed hotspots based on zoom level (individual, clustered, or city marker)
  // Uses displayHotspots (filteredHotspots minus club-claimed when club marker is visible)
  const getProcessedHotspots = () => {
    const validHotspots = displayHotspots;
    
    if (currentZoom >= ZOOM_INDIVIDUAL) {
      // Show individual markers
      return { mode: 'individual', data: validHotspots };
    } else if (currentZoom >= ZOOM_CLUSTER) {
      // Show clustered markers
      const clusters = clusterHotspots(validHotspots, currentZoom);
      return { mode: 'clustered', data: clusters };
    } else {
      // Show single city marker
      return {
        mode: 'city',
        data: [{
          id: 'toronto-city',
          latitude: TORONTO_CENTER.latitude,
          longitude: TORONTO_CENTER.longitude,
          count: validHotspots.length,
          name: 'Toronto',
        }]
      };
    }
  };

  // Get players within 50m of a specific hotspot
  const getPlayersNearHotspot = (hotspot) => {
    if (!hotspot || !nearbyUsers) return [];
    
    return nearbyUsers.filter(user => {
      if (!user.latitude || !user.longitude) return false;
      const distance = calculateDistance(
        hotspot.latitude,
        hotspot.longitude,
        user.latitude,
        user.longitude
      );
      return distance <= 50; // 50 meters
    });
  };

  // Check if a player should be hidden (within 50m of any hotspot)
  const shouldHidePlayer = (player) => {
    // Only apply this overlap rule when individual hotspot pins are visible.
    // At lower zoom (cluster/city modes), applying it causes user-marker flicker
    // as viewport hotspot batches update during pan/zoom.
    if (currentZoom < ZOOM_INDIVIDUAL) {
      return false;
    }
    return hotspots.some(hotspot => {
      const distance = calculateDistance(
        hotspot.latitude,
        hotspot.longitude,
        player.latitude,
        player.longitude
      );
      return distance <= 50; // 50 meters
    });
  };

  const getMarkerColor = (accountType) => {
    return accountType === 'CLUB' ? '#76AF87' : '#94EB98';
  };

  // Apply filters to users/clubs - memoized to avoid recalculating on every render
  const filteredUsers = useMemo(() => {
    try {
      // Safety check - ensure nearbyUsers is defined
      if (!nearbyUsers) {
        return [];
      }
      
      const users = Array.isArray(nearbyUsers) ? nearbyUsers : [];
      
      // Return empty array if users is not an array
      if (!Array.isArray(users)) {
        return [];
      }

      // If no active filters, return users as-is
      if (!activeFilters || typeof activeFilters !== 'object' || Object.keys(activeFilters).length === 0) {
        return users;
      }

      return users.filter(user => {
        // Safety check - skip if user is null/undefined
        if (!user) {
          return false;
        }

        // ── Radius filter (applies to ALL markers) ──
        if (activeFilters.radius != null && location) {
          const R = 6371; // Earth's radius in km
          const lat = user.latitude;
          const lng = user.longitude;
          if (lat && lng) {
            const dLat = (lat - location.latitude) * Math.PI / 180;
            const dLon = (lng - location.longitude) * Math.PI / 180;
            const a =
              Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(location.latitude * Math.PI / 180) * Math.cos(lat * Math.PI / 180) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            if (R * c > activeFilters.radius) {
              return false;
            }
          }
        }

        // Only filter clubs (CLUB account type) for the remaining criteria
        if (user.accountType !== 'CLUB') {
          return true; // Don't filter players by tags/tier/etc.
        }

        // Filter by tags (match against club name, about, address - clubs may not have a tags array)
        if (activeFilters.tags && Array.isArray(activeFilters.tags) && activeFilters.tags.length > 0) {
          const searchableText = [
            user.clubName || '',
            user.about || '',
            user.address || '',
          ].join(' ').toLowerCase();
          const tagWords = activeFilters.tags
            .map(t => (t || '').toLowerCase().replace(/-/g, ' '))
            .filter(Boolean);
          const matchMode = activeFilters.tagsMatchMode || 'any';
          let matchesTag;
          if (matchMode === 'all') {
            matchesTag = tagWords.length > 0 && tagWords.every(tag => searchableText.includes(tag));
          } else {
            matchesTag = tagWords.some(tag => searchableText.includes(tag));
          }
          if (!matchesTag) {
            return false;
          }
        }

        // Filter by tier
        if (activeFilters.tier) {
          if (user.tier !== activeFilters.tier) {
            return false;
          }
        }

        // Filter by has schedule
        if (activeFilters.hasSchedule !== null && activeFilters.hasSchedule !== undefined) {
          const userHasSchedule = !!(user.schedule && user.schedule.trim() !== '');
          if (userHasSchedule !== activeFilters.hasSchedule) {
            return false;
          }
        }

        // Filter by activity level (derived from schedule)
        if (activeFilters.activityLevel) {
          const schedule = (user.schedule || '').toLowerCase();
          const scheduleExists = schedule.trim() !== '';
          let derivedLevel = 'LOW';
          if (scheduleExists) {
            const dayCount = (schedule.match(/(monday|tuesday|wednesday|thursday|friday|saturday|sunday)/gi) || []).length;
            derivedLevel = dayCount >= 3 ? 'HIGH' : 'MEDIUM';
          }
          if (derivedLevel !== activeFilters.activityLevel) {
            return false;
          }
        }

        return true;
      });
    } catch (error) {
      console.error('Error filtering users:', error);
      // Return unfiltered users if filtering fails
      return nearbyUsers || [];
    }
  }, [nearbyUsers, activeFilters, location]);

  // Map filter tag strings to hotspot category (for filtering venue markers)
  const TAG_TO_HOTSPOT_CATEGORY = {
    bar: 'BAR',
    'coffee-shop': 'CAFE',
    cafe: 'CAFE',
    library: 'LIBRARY',
    park: 'PARK',
    club: 'CLUB',
    university: 'UNIVERSITY',
    'public-square': 'PUBLIC_SQUARE',
    other: 'OTHER',
  };

  // Apply same tag filters to hotspots (venue markers) - memoized
  const filteredHotspots = useMemo(() => {
    try {
      if (!hotspots || !Array.isArray(hotspots)) {
        return [];
      }
      let list = hotspots.filter(h => h.latitude && h.longitude);

      // ── Radius filter ──
      if (activeFilters?.radius != null && location) {
        const R = 6371;
        list = list.filter(h => {
          const dLat = (h.latitude - location.latitude) * Math.PI / 180;
          const dLon = (h.longitude - location.longitude) * Math.PI / 180;
          const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(location.latitude * Math.PI / 180) * Math.cos(h.latitude * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
          return R * c <= activeFilters.radius;
        });
      }

      // ── Venue has social (Instagram / Twitter / Facebook) ──
      if (activeFilters?.hasSocialLink === 'instagram') {
        list = list.filter(h => (h.instagram_url || '').trim() !== '');
      } else if (activeFilters?.hasSocialLink === 'twitter') {
        list = list.filter(h => (h.twitter_url || '').trim() !== '');
      } else if (activeFilters?.hasSocialLink === 'facebook') {
        list = list.filter(h => (h.facebook_url || '').trim() !== '');
      }

      // No tag filters -> return (radius + social filtered) list
      if (!activeFilters?.tags?.length) {
        return list;
      }
      const tagWords = (activeFilters.tags || [])
        .map(t => (t || '').toLowerCase().replace(/-/g, ' '))
        .filter(Boolean);
      const matchMode = activeFilters.tagsMatchMode || 'any';

      return list.filter(hotspot => {
        const searchableText = [
          hotspot.name || '',
          hotspot.description || '',
          hotspot.address || '',
          (hotspot.tags || []).join(' '),
        ].join(' ').toLowerCase();
        const category = (hotspot.category || '').toUpperCase();

        const tagMatches = tagWords.map(tag => {
          const asCategory = TAG_TO_HOTSPOT_CATEGORY[tag.replace(/\s+/g, '-')] || TAG_TO_HOTSPOT_CATEGORY[tag];
          if (asCategory && category === asCategory) return true;
          return searchableText.includes(tag);
        });

        if (matchMode === 'all') {
          return tagMatches.length > 0 && tagMatches.every(Boolean);
        }
        return tagMatches.some(Boolean);
      });
    } catch (err) {
      console.error('Error filtering hotspots:', err);
      return hotspots || [];
    }
  }, [hotspots, activeFilters, location]);

  // Hide hotspot marker when the venue is claimed by a club that is also shown as a user (club) marker — keep only the club marker
  const displayHotspots = useMemo(() => {
    const clubIds = new Set(
      (filteredUsers || []).filter(u => u.accountType === 'CLUB').map(u => u.id)
    );
    return filteredHotspots.filter(h => {
      const cid = h.claimed_by || h.claimedBy;
      if (!cid) return true;
      return !clubIds.has(cid);
    });
  }, [filteredHotspots, filteredUsers]);

  // Calculate visible marker counts (for legend display)
  const getVisibleMarkerCounts = () => {
    const usersToCount = filteredUsers || [];
    
    // Count visible users (excluding those in privacy zone, hidden near hotspots, or without coordinates)
    const usersWithoutLatLng = usersToCount.filter(u => !u.latitude || !u.longitude);
    const usersHiddenByHotspots = usersToCount.filter(u => {
      if (!u.latitude || !u.longitude) return false;
      return shouldHidePlayer(u);
    });
    const visibleUsers = usersToCount.filter(user => {
      if (!user.latitude || !user.longitude) return false;
      if (isInsidePrivacyZone(user.latitude, user.longitude)) return false;
      if (shouldHidePlayer(user)) return false;
      return true;
    });
    
    // Count visible hotspots (excluding those without coordinates)
    const hotspotsWithoutLatLng = hotspots.filter(h => !h.latitude || !h.longitude);
    const visibleHotspots = hotspots.filter(hotspot => {
      return hotspot.latitude && hotspot.longitude;
    });
    
    
    return {
      visibleUsers: visibleUsers.length,
      visibleHotspots: visibleHotspots.length,
    };
  };

  const renderUserMarker = (userData, index, isExiting = false) => {
    if (!userData.latitude || !userData.longitude) {
      return null;
    }

    // Hide player if inside privacy zone (e.g. near your house when testing)
    if (isInsidePrivacyZone(userData.latitude, userData.longitude)) {
      return null;
    }

    // Hide player if they're within 50m of any hotspot (clubs are exempt — they pick their own spot)
    if (userData.accountType !== 'CLUB') {
      const shouldHide = shouldHidePlayer(userData);
      if (shouldHide) {
        console.log(`🙈 Hiding player ${userData.username || userData.clubName} - near hotspot`);
        return null;
      }
    }

    const title = userData.accountType === 'CLUB' ? userData.clubName : userData.username;
    const description = userData.accountType === 'CLUB' 
      ? 'Chess Club'
      : `Rating: ${userData.rating || 1200}`;

    // ── Determine display coordinates ──
    // Clubs: use exact location (they chose their pin spot).
    // Players (other than self): apply privacy fuzzing so zooming in never
    //   reveals an exact home address.
    let displayLat = userData.latitude;
    let displayLng = userData.longitude;

    if (userData.accountType !== 'CLUB' && userData.id !== currentUserDbId) {
      // Privacy fuzz – fixed 200m radius, same at every zoom level
      const fuzzed = getFuzzedLocation(userData.latitude, userData.longitude, userData.id);
      displayLat = fuzzed.latitude;
      displayLng = fuzzed.longitude;
    } else {
      // Small overlap-prevention offset for clubs (based on ID hash)
      const idHash = userData.id.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
      displayLat += (idHash % 100) * 0.00001;
      displayLng += ((idHash * 7) % 100) * 0.00001;
    }

    const markerColor = getMarkerColor(userData.accountType);
    
    // Check if this club has an event tonight — only show ripple for approved clubs
    const hasEventTonight = userData.accountType === 'CLUB' && userData.approvalStatus === 'APPROVED' && clubsWithEventsTonight.has(userData.id);

    return (
    <MapMarker
        key={userData.id || index}
      isExiting={isExiting}
      zoomLevel={currentZoom}
      coordinate={{
        latitude: displayLat,
        longitude: displayLng,
      }}
      title={title}
      description={description}
      pinColor={markerColor}
      userData={userData}
      hasEventTonight={hasEventTonight}
      unreadCount={userData.unreadCount || 0}
        onPress={async () => {
          console.log('🎯 Marker clicked, starting animation...');
          // Close any other open popups so only one is ever visible
          setShowHotspotPopup(false);
          setSelectedHotspot(null);
          setShowTournamentPopup(false);
          setSelectedTournament(null);
          setShowPopup(false);
          setSelectedUserId(null);
          setSelectedUserData(null);

          // For clubs, show a hotspot-style popup instead of a player profile popup
          if (userData.accountType === 'CLUB') {
            let clubHotspot = null;
            try {
              clubHotspot = await getHotspotByClaimedBy(userData.id);
            } catch (e) {
              console.warn('Could not load club hotspot for popup:', e);
            }
            const syntheticHotspot = {
              id: userData.id,
              name: userData.clubName || userData.username || 'Chess Club',
              description: userData.about || '',
              address: userData.address || '',
              city: userData.city || '',
              category: 'CLUB',
              latitude: userData.latitude,
              longitude: userData.longitude,
              schedule: userData.schedule || null,
              activityLevel: null,
              amenities: [],
              photos: clubHotspot?.photos || [],
              // Treat the club user id as the "claiming" club for this
              // synthetic hotspot so social sync can resolve the correct
              // clubId when fetching recent posts.
              claimed_by: userData.id,
            };
            handleHotspotMarkerPress(syntheticHotspot);
            return;
          }

          // Ensure UserProfilePopup receives a userId so it can load data
          setSelectedUserId(userData.id);
          
          // Animate map so popup center ends up at viewport center (uses actual viewport size)
          if (mapRef.current) {
            const map = mapRef.current;
            const markerLat = displayLat;
            const markerLng = displayLng;
            console.log('🗺️ Starting 500ms animation to center popup in viewport');
            popupOpeningRef.current = true; // block idle-triggered marker fetch until popup is shown
            
            const { lat: targetLat, lng: targetLng } = getMapCenterForPopupCentered(map, markerLat, markerLng, 'user');
            
            // Get current position
            const currentCenter = map.getCenter();
            const startLat = currentCenter.lat();
            const startLng = currentCenter.lng();
            
            // Custom smooth animation over 500ms
            const duration = 500;
            const startTime = Date.now();
            
            const animate = () => {
              const elapsed = Date.now() - startTime;
              const progress = Math.min(elapsed / duration, 1);
              
              // Easing function (ease-in-out)
              const easeProgress = progress < 0.5
                ? 2 * progress * progress
                : 1 - Math.pow(-2 * progress + 2, 2) / 2;
              
              // Interpolate position
              const currentLat = startLat + (targetLat - startLat) * easeProgress;
              const currentLng = startLng + (targetLng - startLng) * easeProgress;
              
              // Use setCenter instead of panTo to avoid conflicting animations
              map.setCenter({ lat: currentLat, lng: currentLng });
              
              if (progress < 1) {
                requestAnimationFrame(animate);
              } else {
                // Animation complete - update region state to prevent map from jumping back
                // Skip syncing center to map for one cycle so overlay doesn't shift
                setSkipCenterSyncForPopupOpen(true);
                setRegion({
                  latitude: targetLat,
                  longitude: targetLng,
                  latitudeDelta: 0.005,
                  longitudeDelta: 0.005,
                });
                
                console.log('🎬 Animation complete. Final center:', map.getCenter().toJSON());
                console.log('📍 Target was:', { lat: targetLat, lng: targetLng });
                console.log('🎈 Popup will be anchored to:', { lat: markerLat, lng: markerLng });
                
                // Set selectedUserData so popup has coordinates; defer showing popup by 2 frames to avoid shift glitch
                setSelectedUserData({
                  ...userData,
                  latitude: displayLat,
                  longitude: displayLng,
                });
                
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    popupOpeningRef.current = false;
                    setShowPopup(true);
                    setIsViewingPopup(true);
                    setSkipCenterSyncForPopupOpen(false);
                    console.log('✨ Popup shown. Map center now:', map.getCenter().toJSON());
                  });
                });
              }
            };
            
            animate();
          } else {
            // Fallback if no map ref - show immediately
            popupOpeningRef.current = false;
            setSelectedUserData({
              ...userData,
              latitude: displayLat,
              longitude: displayLng,
            });
            setShowPopup(true);
            setIsViewingPopup(true); // Prevent auto-centering
          }
        }}
      />
    );
  };

  const handleSendMessage = (userData) => {
    // Navigate to the Chat tab and pass the user data to open/create conversation
    navigation.navigate('Chat', { 
      openUserId: userData.id,
      openUser: userData,
    });
  };

  // Helper function to reset all Quick Pair states
  const resetQuickPairStates = () => {
    console.log('🧹 Resetting all Quick Pair states');
    setQuickPairSearching(false);
    setQuickPairMatchFound(false);
    setShowQuickPairSettings(false);
    setMyQuickPairRequest(null);
    setMatchedUser(null);
    setMatchedUserRequestId(null);
    setMatchDistance(0);
    setMatchPhase(1);
    setSearchTimeRemaining(30);
    
    // Clear all intervals
    clearInterval(searchIntervalRef.current);
    clearInterval(timerIntervalRef.current);
    clearInterval(matchFoundPollingRef.current);
  };

  // Toggle location sharing (public visibility)
  const handleToggleLocation = async () => {
    const newLocationEnabled = !locationEnabled;
    setLocationEnabled(newLocationEnabled);
    const isClub = appUser?.accountType === 'CLUB';
    
    if (newLocationEnabled) {
      showToast('Location sharing enabled - You are now visible to others', 'success');
      if (locationOffBannerTimerRef.current) {
        clearTimeout(locationOffBannerTimerRef.current);
        locationOffBannerTimerRef.current = null;
      }
      setShowLocationOffBanner(false);
      // Update location in database to make it public (sets isOnline: true)
      if (location && currentUserDbId) {
        updateUserLocation();
      }
    } else {
      showToast('Location sharing disabled - You are now private', 'info');
      setShowLocationOffBanner(true);
      setNearbyUsers([]);
      // Set isOnline to false when location sharing is disabled
      if (currentUserDbId) {
        try {
          // For clubs, only set offline status; never clear their pinned lat/lng
          if (isClub) {
            await updateUserSupabase(currentUserDbId, {
              is_online: false,
            });
          } else {
            await updateUserSupabase(currentUserDbId, {
              is_online: false,
              latitude: null,
              longitude: null,
            });
          }
          console.log('✅ User set to offline');
        } catch (error) {
          console.error('❌ Error setting user offline:', error);
        }
      }
    }
  };

  // Toggle location_visibility between 'everyone' and 'friends_only'
  const handleToggleLocationVisibility = async () => {
    const newValue = locationVisibility === 'everyone' ? 'friends_only' : 'everyone';
    setLocationVisibility(newValue);
    try {
      await updateUserSupabase(currentUserDbId, { location_visibility: newValue });
      if (updateUserProfile) {
        updateUserProfile({ location_visibility: newValue }).catch(() => {});
      }
      showToast(
        newValue === 'friends_only'
          ? 'Location visible to friends only'
          : 'Location visible to everyone',
        'success',
      );
    } catch (err) {
      console.error('Error updating location visibility:', err);
      setLocationVisibility(locationVisibility); // revert on failure
      showToast('Failed to update visibility', 'error');
    }
  };

  // Quick Pair helper functions - defined first so they can be used by other functions
  const deleteQuickPairRequest = async (requestId) => {
    try {
      await deactivateQuickPairRequestSupabase(requestId);
      console.log('🗑️ Quick Pair request deleted:', requestId);
    } catch (error) {
      console.error('Error deleting Quick Pair request:', error);
    }
  };

  const cleanupStaleRequests = async () => {
    try {
      console.log('🧹 Running background cleanup of stale requests...');
      
      const allRequests = await getActiveQuickPairRequests();
      
      const now = new Date();
      const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);
      
      const staleRequests = allRequests.filter(req => {
        const reqDate = new Date(req.created_at);
        const isStale = reqDate < fiveMinutesAgo;
        
        // Don't delete requests from other users that are still fresh (< 5 min)
        // This prevents race condition on same-device testing
        if (!isStale) {
          return false;
        }
        
        const ageMinutes = Math.floor((now - reqDate) / 60000);
        console.log(`  🗑️ Found stale request: ${req.id.substring(0, 8)}... (${ageMinutes} minutes old)`);
        return true;
      });
      
      if (staleRequests.length > 0) {
        console.log(`🗑️ Deleting ${staleRequests.length} stale request(s) older than 5 minutes`);
        for (const req of staleRequests) {
          await deleteQuickPairRequest(req.id);
        }
      } else {
        console.log('✅ No stale requests to clean up');
      }
    } catch (error) {
      console.error('Error cleaning up stale requests:', error);
    }
  };

  // Quick Pair functions
  const handleQuickPairPress = () => {
    // Reset all states before opening settings (clean slate)
    console.log('🎯 Opening Quick Pair - resetting states');
    resetQuickPairStates();
    setShowQuickPairSettings(true);
  };

  const handleStartQuickPairSearch = async (settings) => {
    console.log('🎯 Starting Quick Pair search with:', settings);
    setShowQuickPairSettings(false);
    
    // Close any open profile popups
    setShowPopup(false);
    setSelectedUserId(null);
    setSelectedUserData(null);
    setIsViewingPopup(false);
    
    // Store settings for potential retry
    setLastQuickPairSettings(settings);
    
    // Check authentication first
    if (!appUser || !currentUserDbId) {
      showToast('Please log in to use Quick Pair', 'error');
      return;
    }
    
    if (!location) {
      showToast('Unable to start Quick Pair. Location not available.', 'error');
      return;
    }

    try {
      // CLEANUP: Delete any old requests from this user before creating a new one
      console.log('🧹 Cleaning up any old requests from this user...');
      const allActiveRequests = await getActiveQuickPairRequests();
      const oldRequests = allActiveRequests.filter(req => req.user_id === currentUserDbId);
      
      if (oldRequests.length > 0) {
        console.log(`🗑️ Deleting ${oldRequests.length} old request(s) from this user`);
        for (const oldReq of oldRequests) {
          await deleteQuickPairRequest(oldReq.id);
        }
      }
      
      // Also run background cleanup of all stale requests (>5 min old)
      await cleanupStaleRequests();
      
      // Fetch my rating from appUser (already available)
      const myUserRating = appUser?.inPersonRating || 1200;
      setMyRating(myUserRating);
      setMyProfilePictureUrl(getProfilePictureUrl(appUser?.profilePictureUrl) ?? null);

      // Create QuickPairRequest using Supabase
      const requestInput = {
        user_id: currentUserDbId,
        latitude: location.latitude,
        longitude: location.longitude,
        game_type: settings.gameType,
        time_format: settings.timeFormat,
        max_distance: settings.maxDistance,
        is_active: true,
      };

      const createdRequest = await createQuickPairRequestSupabase(requestInput);
      // Convert snake_case to camelCase for compatibility
      const camelRequest = {
        id: createdRequest.id,
        userId: createdRequest.user_id,
        latitude: createdRequest.latitude,
        longitude: createdRequest.longitude,
        gameType: createdRequest.game_type,
        timeFormat: createdRequest.time_format,
        maxDistance: createdRequest.max_distance,
        isActive: createdRequest.is_active,
        createdAt: createdRequest.created_at,
      };
      setMyQuickPairRequest({ ...camelRequest, userRating: myUserRating });
      console.log('✅ Quick Pair request created:');
      console.log('   ID:', camelRequest.id);
      console.log('   UserID:', camelRequest.userId);
      console.log('   GameType:', camelRequest.gameType);
      console.log('   TimeFormat:', camelRequest.timeFormat);
      console.log('   IsActive:', camelRequest.isActive);
      console.log('   CreatedAt:', camelRequest.createdAt);

      // Small delay to ensure request is fully propagated in database
      // This helps with same-device testing where both accounts start simultaneously
      console.log('⏳ Waiting 500ms for database propagation...');
      await new Promise(resolve => setTimeout(resolve, 500));
      console.log('✅ Delay complete, starting search');

      // Start searching
      setQuickPairSearching(true);
      setSearchTimeRemaining(30);
      setMatchPhase(1);
      
      console.log('🚀 Search started! Polling for matches every 2 seconds...');

      // Start timer
      timerIntervalRef.current = setInterval(() => {
        setSearchTimeRemaining((prev) => {
          if (prev <= 1) {
            handleQuickPairTimeout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Start search polling
      // Use camelRequest (already converted) instead of createdRequest (raw Supabase response)
      searchIntervalRef.current = setInterval(() => {
        performMatchSearch(camelRequest, myUserRating, settings);
      }, 2000); // Poll every 2 seconds

    } catch (error) {
      console.error('Error starting Quick Pair:', error);
      showToast('Failed to start Quick Pair', 'error');
    }
  };

  const performMatchSearch = async (myRequest, myUserRating, settings) => {
    try {
      // Determine current phase based on time remaining
      let currentPhase = 1;
      if (searchTimeRemaining <= 15) currentPhase = 2;
      if (searchTimeRemaining <= 5) currentPhase = 3;
      
      setMatchPhase(currentPhase);

      // Fetch all active Quick Pair requests using Supabase
      const allRequestsRaw = await getActiveQuickPairRequests();
      // Convert snake_case to camelCase for compatibility
      const allRequests = allRequestsRaw.map(req => ({
        id: req.id,
        userId: req.user_id,
        latitude: req.latitude,
        longitude: req.longitude,
        gameType: req.game_type,
        timeFormat: req.time_format,
        maxDistance: req.max_distance,
        isActive: req.is_active,
        createdAt: req.created_at,
        user: req.user ? {
          id: req.user.id,
          username: req.user.username,
          inPersonRating: req.user.in_person_rating,
          profilePictureUrl: req.user.profile_picture_url,
        } : null,
      }));

      console.log(`🔍 Phase ${currentPhase}: Found ${allRequests.length} requests from query`);
      console.log(`🔑 My userDbId: ${currentUserDbId}`);

      // Filter out stale requests and my own request
      const now = new Date();
      const twoMinutesAgo = new Date(now.getTime() - 2 * 60 * 1000);
      
      const freshRequests = allRequests.filter(req => {
        const reqDate = new Date(req.createdAt);
        const ageSeconds = Math.floor((now - reqDate) / 1000);
        
        console.log(`  📋 Checking request from ${req.user?.username || 'unknown'}: ID=${req.id.substring(0, 8)}..., userId=${req.userId.substring(0, 8)}..., age=${ageSeconds}s, isActive=${req.isActive}`);
        
        // Skip my own request (compare as strings to handle UUID format differences)
        if (String(req.userId) === String(currentUserDbId)) {
          console.log(`  ⏭️ Skipping my own request (userId match: ${req.userId} === ${currentUserDbId})`);
          return false;
        }
        
        // Skip stale requests (older than 2 minutes) - but be lenient for testing (90 seconds)
        if (reqDate < twoMinutesAgo) {
          console.log(`  ⏭️ Skipping stale request (${ageSeconds}s old, threshold: 120s)`);
          return false;
        }
        
        // Skip inactive requests (already accepted by someone)
        if (!req.isActive) {
          console.log(`  ⏭️ Skipping inactive request (isActive=false)`);
          return false;
        }
        
        console.log(`  ✅ Request PASSED all filters`);
        return true;
      });

      console.log(`🔍 Phase ${currentPhase}: ${freshRequests.length} fresh, active requests (filtered from ${allRequests.length})`);

      // Find matches
      const matches = findMatches(
        { ...myRequest, userRating: myUserRating },
        freshRequests,
        currentPhase
      );

      console.log(`✅ Found ${matches.length} potential matches in phase ${currentPhase}`);

      if (matches.length > 0) {
        // Pick the best match (first in sorted list)
        const bestMatch = matches[0];
        console.log('🎉 Match found:', bestMatch.user.username, bestMatch.distance.toFixed(2), 'km away');
        
        setMatchedUser({
          ...bestMatch.user,
          profilePictureUrl: getProfilePictureUrl(bestMatch.user?.profilePictureUrl) || bestMatch.user?.profilePictureUrl,
        });
        setMatchedUserRequestId(bestMatch.id); // Store the opponent's request ID
        setMatchDistance(bestMatch.distance);
        setQuickPairMatchFound(true);
        
        // Stop searching
        clearInterval(searchIntervalRef.current);
        clearInterval(timerIntervalRef.current);
        
        // Start polling to check if opponent has already accepted
        // Pass bestMatch.user directly since state update is async
        startMatchFoundPolling(bestMatch.id);
      }
    } catch (error) {
      console.error('Error searching for matches:', error);
    }
  };

  // Simple polling: just check if opponent is still searching (hasn't cancelled)
  const startMatchFoundPolling = (opponentRequestId) => {
    console.log('👀 Monitoring opponent status...');
    
    matchFoundPollingRef.current = setInterval(async () => {
      try {
        // Check if opponent's request still exists
        const opponentRequestRaw = await getQuickPairRequestById(opponentRequestId);
        
        if (!opponentRequestRaw) {
          // Opponent cancelled - restart search
          console.log('❌ Opponent left, finding new match...');
          clearInterval(matchFoundPollingRef.current);
          
          setQuickPairMatchFound(false);
          setMatchedUser(null);
          setMatchedUserRequestId(null);
          
          if (lastQuickPairSettings) {
            showToast('Opponent left. Finding new match...', 'info');
            setTimeout(() => {
              handleStartQuickPairSearch(lastQuickPairSettings);
            }, 500);
          }
        }
        // If request exists (active or inactive), opponent is still there
      } catch (error) {
        console.error('Error checking opponent status:', error);
      }
    }, 3000); // Check every 3 seconds
  };

  const handleQuickPairTimeout = () => {
    console.log('⏱️ Quick Pair search timed out');
    clearInterval(searchIntervalRef.current);
    clearInterval(timerIntervalRef.current);
    setQuickPairSearching(false);
    
    // Delete my request
    if (myQuickPairRequest) {
      deleteQuickPairRequest(myQuickPairRequest.id);
    }
    
    showToast('No match found. Try expanding your search radius or trying again later.', 'info');
  };

  const handleQuickPairCancel = async () => {
    console.log('❌ Quick Pair cancelled/declined');
    
    // Clear all intervals
    clearInterval(searchIntervalRef.current);
    clearInterval(timerIntervalRef.current);
    clearInterval(matchFoundPollingRef.current);
    
    // Delete my request if it exists (signals I'm not interested)
    if (myQuickPairRequest) {
      try {
        await deleteQuickPairRequest(myQuickPairRequest.id);
        console.log('✅ Deleted my Quick Pair request');
      } catch (error) {
        console.error('Error deleting request:', error);
      }
    }
    
    // Run background cleanup of stale requests
    cleanupStaleRequests();
    
    // Reset all states
    setQuickPairSearching(false);
    setQuickPairMatchFound(false);
    setMatchedUser(null);
    setMatchedUserRequestId(null);
    setMyQuickPairRequest(null);
    
    showToast('Quick Pair cancelled', 'info');
  };

  // Simple: User clicked "Send Message" - just navigate to chat
  const handleQuickPairSendMessage = () => {
    console.log('💬 Send message clicked');
    
    const opponentId = matchedUser?.id;
    const myRequestId = myQuickPairRequest?.id;
    
    if (!opponentId) {
      console.error('❌ No matched user');
      showToast('Error: No match found. Please try again.', 'error');
      resetQuickPairStates();
      return;
    }
    
    // Stop any polling
    clearInterval(matchFoundPollingRef.current);
    clearInterval(searchIntervalRef.current);
    clearInterval(timerIntervalRef.current);
    
    // Clean up my request (in background, don't wait)
    if (myRequestId) {
      deleteQuickPairRequest(myRequestId).catch(e => console.log('ℹ️ Request cleanup:', e));
    }
    
    // Reset states
    resetQuickPairStates();
    
    // Navigate to chat with the matched user
    navigation.navigate('Chat', {
      openUserId: opponentId,
      openUser: matchedUser,
    });
    
    showToast(`Matched with ${matchedUser?.username}! 🎉`, 'success');
  };

  // Handle hotspot marker click
  const handleHotspotMarkerPress = (hotspot) => {
    console.log('🏛️ Hotspot marker clicked:', hotspot.name);
    
    // Close any other open popups so only one is ever visible
    setShowPopup(false);
    setSelectedUserId(null);
    setSelectedUserData(null);
    setShowTournamentPopup(false);
    setSelectedTournament(null);
    resetQuickPairStates();

    setIsViewingPopup(true);

    // Center map so hotspot popup center is at viewport center (uses actual viewport size)
    if (mapRef.current && hotspot.latitude && hotspot.longitude) {
      const map = mapRef.current;
      
      console.log('🗺️ Starting animation for hotspot popup (viewport-centered)');
      popupOpeningRef.current = true; // block idle-triggered marker fetch until popup is shown
      
      const { lat: targetLat, lng: targetLng } = getMapCenterForPopupCentered(map, hotspot.latitude, hotspot.longitude, 'hotspot');
      
      // Get current position
      const currentCenter = map.getCenter();
      const startLat = currentCenter.lat();
      const startLng = currentCenter.lng();
      
      // Custom smooth animation over 500ms
      const duration = 500;
      const startTime = Date.now();
      
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function (ease-in-out)
        const easeProgress = progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        
        // Interpolate position
        const currentLat = startLat + (targetLat - startLat) * easeProgress;
        const currentLng = startLng + (targetLng - startLng) * easeProgress;
        
        map.setCenter({ lat: currentLat, lng: currentLng });
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          // Animation complete - update region state to prevent map from jumping back
          // Skip syncing center to map for one cycle so overlay doesn't shift (library would call setCenter again)
          setSkipCenterSyncForPopupOpen(true);
          setRegion({
            latitude: targetLat,
            longitude: targetLng,
            latitudeDelta: 0.005,
            longitudeDelta: 0.005,
          });
          
          console.log('🎬 Hotspot animation complete');
          
          // Defer popup mount by 2 frames so map has painted; then re-enable center sync
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              popupOpeningRef.current = false;
              setSelectedHotspot(hotspot);
              setShowHotspotPopup(true);
              setSkipCenterSyncForPopupOpen(false);
            });
          });
        }
      };
      
      animate();
    } else {
      // No animation, show immediately
      popupOpeningRef.current = false;
      setSelectedHotspot(hotspot);
      setShowHotspotPopup(true);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (searchIntervalRef.current) clearInterval(searchIntervalRef.current);
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      if (matchFoundPollingRef.current) clearInterval(matchFoundPollingRef.current);
      if (mapIdleDebounceRef.current) clearTimeout(mapIdleDebounceRef.current);
      if (myQuickPairRequest) {
        deleteQuickPairRequest(myQuickPairRequest.id);
      }
    };
  }, [myQuickPairRequest]);

  // Re-center popup when viewport is resized (must run before any conditional return to keep hook count stable)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.addEventListener) return;
    const handleResize = () => {
      const map = mapRef.current;
      if (!map) return;
      if (showPopup && selectedUserData?.latitude != null && selectedUserData?.longitude != null) {
        const { lat: targetLat, lng: targetLng } = getMapCenterForPopupCentered(map, selectedUserData.latitude, selectedUserData.longitude, 'user');
        map.setCenter({ lat: targetLat, lng: targetLng });
        setRegion(prev => prev ? { ...prev, latitude: targetLat, longitude: targetLng } : prev);
      } else if (showHotspotPopup && selectedHotspot?.latitude != null && selectedHotspot?.longitude != null) {
        const { lat: targetLat, lng: targetLng } = getMapCenterForPopupCentered(map, selectedHotspot.latitude, selectedHotspot.longitude, 'hotspot');
        map.setCenter({ lat: targetLat, lng: targetLng });
        setRegion(prev => prev ? { ...prev, latitude: targetLat, longitude: targetLng } : prev);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showPopup, showHotspotPopup, selectedUserData?.latitude, selectedUserData?.longitude, selectedHotspot?.latitude, selectedHotspot?.longitude]);

  // Handle map drag start
  const handleMapDragStart = () => {
    isDraggingRef.current = true;
    // Kill any pending idle debounce so it can't fire mid-drag
    if (mapIdleDebounceRef.current) {
      clearTimeout(mapIdleDebounceRef.current);
      mapIdleDebounceRef.current = null;
    }
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
    }
    // On web: set state so popups can freeze in viewport; capture popup marker position for fixed overlay
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.google?.maps) {
      const map = mapRef.current;
      if (map && typeof map.getDiv === 'function' && typeof map.getProjection === 'function') {
        const div = map.getDiv();
        const projection = map.getProjection();
        if (div && projection) {
          let coords = null;
          if (showHotspotPopup && selectedHotspot) {
            const latLng = new window.google.maps.LatLng(selectedHotspot.latitude, selectedHotspot.longitude);
            const pixel = projection.fromLatLngToContainerPixel(latLng);
            const rect = div.getBoundingClientRect();
            coords = { x: rect.left + pixel.x, y: rect.top + pixel.y };
          } else if (showPopup && selectedUserData?.latitude != null && selectedUserData?.longitude != null) {
            const latLng = new window.google.maps.LatLng(selectedUserData.latitude, selectedUserData.longitude);
            const pixel = projection.fromLatLngToContainerPixel(latLng);
            const rect = div.getBoundingClientRect();
            if (isMobile) {
              coords = { x: rect.left + pixel.x, y: rect.top + pixel.y };
            } else {
              const viewportWidth = div.offsetWidth;
              const viewportHeight = div.offsetHeight;
              const popupHeight = getPopupHeight(viewportWidth, viewportHeight, 'user');
              coords = { x: rect.left + pixel.x, y: rect.top + pixel.y - USER_POPUP_ANCHOR_Y + popupHeight };
            }
          } else if (showTournamentPopup && selectedTournament?.club) {
            const lat = selectedTournament.club.latitude ?? 49.2827;
            const lng = selectedTournament.club.longitude ?? -123.1207;
            const latLng = new window.google.maps.LatLng(lat, lng);
            const pixel = projection.fromLatLngToContainerPixel(latLng);
            const rect = div.getBoundingClientRect();
            coords = { x: rect.left + pixel.x, y: rect.top + pixel.y };
          }
          // Flush state update so popup switches to fixed position before map moves (avoids one-frame glitch)
          if (typeof require !== 'undefined') {
            try {
              const { flushSync } = require('react-dom');
              flushSync(() => {
                setIsMapDragging(true);
                setFrozenPopupCoords(coords);
              });
            } catch (_) {
              setIsMapDragging(true);
              setFrozenPopupCoords(coords);
            }
          } else {
            setIsMapDragging(true);
            setFrozenPopupCoords(coords);
          }
        } else {
          setIsMapDragging(true);
          setFrozenPopupCoords(null);
        }
      } else {
        setIsMapDragging(true);
        setFrozenPopupCoords(null);
      }
    }
    // Apply UI fade via DOM to avoid React re-render of all markers
    if (uiFadeEnabled && Platform.OS === 'web' && typeof document !== 'undefined') {
      document.querySelectorAll('[data-ui-fade]').forEach(el => { el.style.opacity = '0.3'; });
    } else if (uiFadeEnabled) {
      setIsMapDragging(true);
    }
  };

  // Handle map drag end
  const handleMapDragEnd = () => {
    isDraggingRef.current = false;
    lastDragEndTimeRef.current = Date.now();
    // Flush deferred zoom/hotspots after map has settled (150ms) so OverlayView positions are stable — avoids ghost
    const pendingZoom = pendingZoomRef.current;
    const pendingHotspots = pendingHotspotsRef.current;
    pendingZoomRef.current = null;
    pendingHotspotsRef.current = null;
    if (pendingZoom != null || pendingHotspots != null) {
      setTimeout(() => {
        if (pendingZoom != null) setCurrentZoom(pendingZoom);
        if (pendingHotspots != null) setHotspots(pendingHotspots);
      }, 150);
    }
    if (Platform.OS === 'web') {
      // Always clear frozen popup and drag state on web when drag ends (so popup returns to overlay)
      if (uiFadeEnabled && typeof document !== 'undefined') {
        dragTimeoutRef.current = setTimeout(() => {
          document.querySelectorAll('[data-ui-fade]').forEach(el => { el.style.opacity = '1'; });
          setIsMapDragging(false);
          setFrozenPopupCoords(null);
        }, 100);
      } else {
        dragTimeoutRef.current = setTimeout(() => {
          setIsMapDragging(false);
          setFrozenPopupCoords(null);
        }, 100);
      }
    } else if (uiFadeEnabled) {
      dragTimeoutRef.current = setTimeout(() => {
        setIsMapDragging(false);
      }, 100);
    }
  };

  // Handle map idle (debounced to prevent fetch storm when panning/zooming rapidly)
  const handleMapIdle = () => {
    // Don't schedule fetch while popup-open animation is running (avoids setHotspots re-render during open)
    if (popupOpeningRef.current) {
      if (mapIdleDebounceRef.current) {
        clearTimeout(mapIdleDebounceRef.current);
        mapIdleDebounceRef.current = null;
      }
      return;
    }
    if (mapIdleDebounceRef.current) {
      clearTimeout(mapIdleDebounceRef.current);
    }
    mapIdleDebounceRef.current = setTimeout(() => {
      mapIdleDebounceRef.current = null;
      if (isDraggingRef.current) return;
      // Avoid fetch right after drag end; let 150ms flush window pass first
      if (Date.now() - lastDragEndTimeRef.current < 150) return;
      // Don't fetch while any popup is open (avoids marker re-render causing overlay shift)
      if (popupOpenRef.current) return;
      if (!location || !currentUserDbId || !appUser) return;
      const map = mapRef.current;
      // NOTE: Do not refetch nearby users on every idle event.
      // It causes marker churn/flicker during pan/zoom due to frequent list updates.
      // Nearby users are already refreshed by initial load and periodic polling.
      if (map && typeof map.getBounds === 'function') {
        try {
          const bounds = map.getBounds();
          if (bounds) {
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            const b = { minLat: sw.lat(), maxLat: ne.lat(), minLng: sw.lng(), maxLng: ne.lng() };
            fetchHotspots(b).catch((err) => {
              if (!err?.message?.includes('No credentials')) {
                console.error('Error in fetchHotspots from handleMapIdle:', err);
              }
            });
          } else {
            // Bounds can be transiently unavailable while map is settling; skip this cycle.
            return;
          }
        } catch {
          // Transient map state while interacting; skip this cycle.
          return;
        }
      } else {
        // Map not ready yet; skip this cycle.
        return;
      }
    }, MAP_IDLE_DEBOUNCE_MS);
  };

  if (loading) {
    return (
      <View style={[styles.loadingContainer, { backgroundColor: colors.background }]}>
        <LoadingAnimation />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={[styles.mapWrapper, (showHotspotPopup || showPopup || showTournamentPopup) && { zIndex: 101 }]}>
      <MapView
        mapRef={mapRef}
        style={styles.map}
        draggable={!(isMobile && (showHotspotPopup || showPopup || showTournamentPopup))}
        initialRegion={region || {
          latitude: 43.6532,  // Toronto
          longitude: -79.3832,
          latitudeDelta: 0.05,
          longitudeDelta: 0.05,
        }}
        region={region}
        skipCenterSyncForPopupOpen={skipCenterSyncForPopupOpen}
        showsUserLocation={true}
        isDarkMode={isDarkMode}
        showStreetLabels={showStreetLabels}
        onDragStart={handleMapDragStart}
        onDragEnd={handleMapDragEnd}
        onZoomChange={(zoom) => {
          if (isDraggingRef.current) {
            pendingZoomRef.current = zoom;
            return;
          }
          if (zoom !== currentZoom) {
            setCurrentZoom(zoom);
          }
        }}
        onIdle={handleMapIdle}
        onMapReady={handleMapReady}
      >
        {/* Current user marker - fuzzed within 200m at all zoom levels */}
        {location && locationDisplay && (
          <MapMarker
            coordinate={locationDisplay}
            title="You"
            description="Your current location"
            pinColor="#94EB98"
            zoomLevel={currentZoom}
          />
        )}

        {/* Player markers – individual when zoom >= 10, clustered when 6–9, hidden at city zoom */}
        {(mapLayerMode === 'all' || mapLayerMode === 'players' || exitingLayer === 'players') && effectiveLocationEnabled && (() => {
          const processed = getProcessedPlayers();
          if (processed.mode === 'hidden') return null;
          if (processed.mode === 'individual') {
            const list = isMobile ? processed.data.slice(0, MOBILE_MAX_USER_MARKERS) : processed.data;
            return list.map((user, index) => renderUserMarker(user, index, exitingLayer === 'players'));
          }
          const clusterList = isMobile ? processed.data.slice(0, MOBILE_MAX_HOTSPOT_MARKERS) : processed.data;
          return clusterList.map((cluster) => (
            <MapMarker
              key={cluster.id}
              isExiting={exitingLayer === 'players'}
              zoomLevel={currentZoom}
              coordinate={{ latitude: cluster.latitude, longitude: cluster.longitude }}
              title={`${cluster.count} players`}
              description="Zoom in to see individual players"
              isCluster={true}
              isPlayerCluster={true}
              clusterCount={cluster.count}
              onPress={() => {
                if (mapRef.current) {
                  mapRef.current.setZoom(ZOOM_INDIVIDUAL);
                  mapRef.current.panTo({ lat: cluster.latitude, lng: cluster.longitude });
                }
              }}
            />
          ));
        })()}

        {/* Club markers – individual when zoom >= 10, clustered when 6–9, hidden at city zoom */}
        {(mapLayerMode === 'all' || mapLayerMode === 'hotspots' || exitingLayer === 'hotspots') && effectiveLocationEnabled && (() => {
          const processed = getProcessedClubs();
          if (processed.mode === 'hidden') return null;
          if (processed.mode === 'individual') {
            return processed.data.map((user, index) => renderUserMarker(user, index, exitingLayer === 'hotspots'));
          }
          const clusterList = isMobile ? processed.data.slice(0, MOBILE_MAX_HOTSPOT_MARKERS) : processed.data;
          return clusterList.map((cluster) => (
            <MapMarker
              key={cluster.id}
              isExiting={exitingLayer === 'hotspots'}
              zoomLevel={currentZoom}
              coordinate={{ latitude: cluster.latitude, longitude: cluster.longitude }}
              title={`${cluster.count} clubs`}
              description="Zoom in to see individual clubs"
              isCluster={true}
              isPlayerCluster={false}
              clusterCount={cluster.count}
              onPress={() => {
                if (mapRef.current) {
                  mapRef.current.setZoom(ZOOM_INDIVIDUAL);
                  mapRef.current.panTo({ lat: cluster.latitude, lng: cluster.longitude });
                }
              }}
            />
          ));
        })()}

        {/* Hotspot markers - show when All/Venues or when exiting (playing exit animation) */}
        {(mapLayerMode === 'all' || mapLayerMode === 'hotspots' || exitingLayer === 'hotspots') && (() => {
          const processed = getProcessedHotspots();
          
          if (processed.mode === 'individual') {
            // Show individual markers at high zoom (limit on mobile for performance)
            const hotspotList = isMobile ? processed.data.slice(0, MOBILE_MAX_HOTSPOT_MARKERS) : processed.data;
            return hotspotList.map((hotspot) => {
              const scale = getHotspotScale(hotspot);
              const activityLevel = getHotspotActivityLevel(hotspot);
              
              return (
                <MapMarker
                  key={`hotspot-${hotspot.id}`}
                  isExiting={exitingLayer === 'hotspots'}
                  coordinate={{ latitude: hotspot.latitude, longitude: hotspot.longitude }}
                  title={hotspot.name}
                  description={hotspot.category}
                  onPress={() => handleHotspotMarkerPress(hotspot)}
                  customMarker={hotspot}
                  isHotspot={true}
                  hotspotScale={scale}
                  activityLevel={activityLevel}
                  zoomLevel={currentZoom}
                />
              );
            });
          } else if (processed.mode === 'clustered') {
            // Show cluster markers at medium zoom (limit on mobile for performance)
            const clusterList = isMobile ? processed.data.slice(0, MOBILE_MAX_HOTSPOT_MARKERS) : processed.data;
            return clusterList.map((cluster) => (
              <MapMarker
                key={cluster.id}
                isExiting={exitingLayer === 'hotspots'}
                zoomLevel={currentZoom}
                coordinate={{ latitude: cluster.latitude, longitude: cluster.longitude }}
                title={`${cluster.count} hotspots`}
                description="Zoom in to see individual hotspots"
                isCluster={true}
                clusterCount={cluster.count}
                clusterHotspots={cluster.hotspots}
                onPress={() => {
                  // Zoom in to show individual markers
                  if (mapRef.current) {
                    mapRef.current.setZoom(ZOOM_INDIVIDUAL);
                    mapRef.current.panTo({ lat: cluster.latitude, lng: cluster.longitude });
                  }
                }}
              />
            ));
          } else {
            // Show single city marker at low zoom
            return processed.data.map((city) => (
              <MapMarker
                key={city.id}
                isExiting={exitingLayer === 'hotspots'}
                zoomLevel={currentZoom}
                coordinate={{ latitude: city.latitude, longitude: city.longitude }}
                title={city.name}
                description={`${city.count} chess hotspots`}
                isCluster={true}
                clusterCount={city.count}
                isCityMarker={true}
                onPress={() => {
                  // Zoom in to show clusters
                  if (mapRef.current) {
                    mapRef.current.setZoom(ZOOM_CLUSTER + 2);
                    mapRef.current.panTo({ lat: city.latitude, lng: city.longitude });
                  }
                }}
              />
            ));
          }
        })()}

        {/* Tournament markers - show when All or Players or when exiting players */}
        {(mapLayerMode === 'all' || mapLayerMode === 'players' || exitingLayer === 'players') && (isMobile ? tournaments.slice(0, MOBILE_MAX_TOURNAMENT_MARKERS) : tournaments).map((tournament) => {
          if (!tournament.club?.latitude || !tournament.club?.longitude) return null;
          
          return (
            <MapMarker
              key={`tournament-${tournament.id}`}
              isExiting={exitingLayer === 'players'}
              zoomLevel={currentZoom}
              coordinate={{ 
                latitude: tournament.club.latitude, 
                longitude: tournament.club.longitude 
              }}
              title={tournament.name}
              description={`${tournament.participants?.items?.length || 0} participants`}
              onPress={() => {
                // Close any other open popups so only one is ever visible
                setShowPopup(false);
                setSelectedUserId(null);
                setSelectedUserData(null);
                setShowHotspotPopup(false);
                setSelectedHotspot(null);
                setSelectedTournament(tournament);
                setShowTournamentPopup(true);
                setIsViewingPopup(true);
              }}
              isTournament={true}
              tournament={tournament}
            />
          );
        })}

        {/* User Profile Popup - Must be inside MapView for OverlayView to work */}
        <UserProfilePopup
          visible={showPopup}
          userId={selectedUserId}
          userCoordinate={selectedUserData ? {
            latitude: selectedUserData.latitude,
            longitude: selectedUserData.longitude
          } : null}
          navigation={navigation}
          initialUserData={selectedUserData}
          isMapDragging={isMapDragging}
          frozenPopupViewport={showPopup ? frozenPopupCoords : null}
          onClose={() => {
            setShowPopup(false);
            setIsViewingPopup(false); // Allow auto-centering again
            setSelectedUserId(null);
            setSelectedUserData(null);
          }}
          onSendMessage={handleSendMessage}
        />

        {/* Hotspot Popup - Must be inside MapView for OverlayView to work */}
        <HotspotPopup
          hotspot={selectedHotspot}
          visible={showHotspotPopup}
          isMapDragging={isMapDragging}
          frozenPopupViewport={showHotspotPopup ? frozenPopupCoords : null}
          onClose={() => {
            setShowHotspotPopup(false);
            setIsViewingPopup(false);
            setSelectedHotspot(null);
          }}
          nearbyPlayers={selectedHotspot ? getPlayersNearHotspot(selectedHotspot) : []}
          onPlayerClick={(player) => {
            // Close other popups and open player popup (only one popup at a time)
            setShowHotspotPopup(false);
            setSelectedHotspot(null);
            setShowTournamentPopup(false);
            setSelectedTournament(null);
            setSelectedUserId(player.id);
            setSelectedUserData(player);
            setShowPopup(true);
          }}
          currentUserDbId={currentUserDbId}
          mapRef={mapRef}
          navigation={navigation}
          onShowDetail={() => {
            setShowHotspotDetailModal(true);
          }}
        />

        {/* Tournament Popup - Must be inside MapView for OverlayView to work */}
        <TournamentPopup
          tournament={selectedTournament}
          visible={showTournamentPopup}
          isMapDragging={isMapDragging}
          frozenPopupViewport={showTournamentPopup ? frozenPopupCoords : null}
          onClose={() => {
            setShowTournamentPopup(false);
            setIsViewingPopup(false);
            setSelectedTournament(null);
          }}
          onViewTournament={() => {
            setShowTournamentPopup(false);
            setIsViewingPopup(false);
            navigation.navigate('TournamentDetail', { tournamentId: selectedTournament?.id });
          }}
          onViewPlayerMode={() => {
            setShowTournamentPopup(false);
            setIsViewingPopup(false);
            navigation.navigate('TournamentPlayer', { tournamentId: selectedTournament?.id });
          }}
        />
      </MapView>
      </View>

      {/* Hotspot Detail Modal */}
      <HotspotDetailModal
        visible={showHotspotDetailModal}
        hotspot={selectedHotspot}
        hotspotId={selectedHotspot?.id}
        clubId={(() => {
          const cid = selectedHotspot?.claimed_by || selectedHotspot?.claimedBy || selectedHotspot?.owner_id || selectedHotspot?.owner?.id;
          // #region agent log
          if (showHotspotDetailModal && selectedHotspot) {
            fetch('http://127.0.0.1:7242/ingest/b52ea7f9-772d-4699-bf57-9767c6a601fb',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'0bf1ae'},body:JSON.stringify({sessionId:'0bf1ae',location:'MapScreen.js:clubId',message:'Modal clubId from selectedHotspot',data:{hotspotId:selectedHotspot?.id,claimed_by:selectedHotspot?.claimed_by,claimedBy:selectedHotspot?.claimedBy,owner_id:selectedHotspot?.owner_id,ownerId:selectedHotspot?.owner?.id,computedClubId:cid},timestamp:Date.now(),hypothesisId:'A'})}).catch(()=>{});
          }
          // #endregion
          return cid;
        })()}
        onClose={() => {
          setShowHotspotDetailModal(false);
        }}
        navigation={navigation}
      />

      {/* Top left controls: Discover, Filter, Layer toggle - responsive row that fits mobile */}
      <View
        dataSet={{ uiFade: true }}
        style={[
          styles.topControlsRow,
          isMobile && styles.topControlsRowMobile,
          styles.uiElementTransition,
          {
            opacity: (uiFadeEnabled && isMapDragging) ? 0.3 : 1,
          },
        ]}
        pointerEvents="box-none"
      >
        <TouchableOpacity
          style={[styles.eventDiscoveryButton, isMobile && styles.eventDiscoveryButtonMobile]}
          onPress={() => navigation.navigate('EventDiscovery')}
          activeOpacity={0.8}
        >
          <Icon name="explore" size={isMobile ? 18 : 20} color="white" />
          <Text style={[styles.eventDiscoveryButtonText, isMobile && styles.eventDiscoveryButtonTextMobile]}>Discover</Text>
        </TouchableOpacity>

        {(() => {
          const activeCount = Object.entries(activeFilters).filter(([k, v]) => {
            if (k === 'tagsMatchMode') return false;
            if (Array.isArray(v)) return v.length > 0;
            if (typeof v === 'string') return v.trim() !== '';
            return v !== null && v !== undefined;
          }).length;
          return (
            <TouchableOpacity
              style={[
                styles.filterButton,
                isMobile && styles.filterButtonMobile,
                activeCount > 0 && styles.filterButtonActive,
              ]}
              onPress={() => setShowFilterModal(true)}
              activeOpacity={0.8}
            >
              <Icon name="filter-list" size={isMobile ? 18 : 20} color="white" />
              <Text style={[styles.filterButtonText, isMobile && styles.filterButtonTextMobile]}>Filter</Text>
              {activeCount > 0 && (
                <View style={[styles.filterBadge, isMobile && styles.filterBadgeMobile]}>
                  <Text style={[styles.filterBadgeText, isMobile && styles.filterBadgeTextMobile]}>{activeCount}</Text>
                </View>
              )}
            </TouchableOpacity>
          );
        })()}

        <View
          style={[
            styles.layerToggle,
            isMobile && styles.layerToggleMobile,
            { backgroundColor: isDarkMode ? 'rgba(45, 45, 45, 0.95)' : 'rgba(255, 255, 255, 0.95)' },
          ]}
        >
          {(['all', 'hotspots', 'players']).map((mode) => (
            <TouchableOpacity
              key={mode}
              style={[
                styles.layerToggleButton,
                isMobile && styles.layerToggleButtonMobile,
                mapLayerMode === mode && styles.layerToggleButtonActive,
              ]}
              onPress={() => setMapLayerMode(mode)}
              activeOpacity={0.8}
            >
              <Text
                style={[
                  styles.layerToggleText,
                  isMobile && styles.layerToggleTextMobile,
                  { color: mapLayerMode === mode ? '#fff' : colors.textSecondary },
                  mapLayerMode === mode && styles.layerToggleTextActive,
                ]}
              >
                {mode === 'all' ? 'All' : mode === 'hotspots' ? 'Venues' : 'Players'}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Map Controls - Top Right (Zoom + Find Me - all users) */}
      <View dataSet={{ uiFade: true }} style={[
        styles.mapControls,
        isMobile && styles.mapControlsMobile,
        styles.uiElementTransition,
        { opacity: (uiFadeEnabled && isMapDragging) ? 0.3 : 1 }
      ]}>
        {/* Zoom In Button */}
        <TouchableOpacity
          style={[styles.mapControlButton, isMobile && styles.mapControlButtonMobile]}
          onPress={() => {
            if (mapRef.current?.getZoom) {
              const currentZoom = mapRef.current.getZoom();
              mapRef.current.setZoom(currentZoom + 1);
            }
          }}
          activeOpacity={0.8}
        >
          <Icon name="add" size={isMobile ? 20 : 24} color="white" />
        </TouchableOpacity>

        {/* Zoom Out Button */}
        <TouchableOpacity
          style={[styles.mapControlButton, isMobile && styles.mapControlButtonMobile]}
          onPress={() => {
            if (mapRef.current?.getZoom) {
              const currentZoom = mapRef.current.getZoom();
              mapRef.current.setZoom(currentZoom - 1);
            }
          }}
          activeOpacity={0.8}
        >
          <Icon name="remove" size={isMobile ? 20 : 24} color="white" />
        </TouchableOpacity>

        {/* Find My Location Button */}
        <TouchableOpacity
          style={[styles.mapControlButton, isMobile && styles.mapControlButtonMobile]}
          onPress={getCurrentLocation}
          activeOpacity={0.8}
        >
          <Icon name="my-location" size={isMobile ? 20 : 24} color="white" />
        </TouchableOpacity>

        {/* Find My Marker Button — club accounts only */}
        {appUser?.accountType === 'CLUB' && clubMarkerLocation && (
          <TouchableOpacity
            style={[styles.mapControlButton, isMobile && styles.mapControlButtonMobile, { backgroundColor: '#76AF87' }]}
            onPress={() => {
              if (mapRef.current && clubMarkerLocation) {
                mapRef.current.panTo({
                  lat: clubMarkerLocation.latitude,
                  lng: clubMarkerLocation.longitude,
                });
                mapRef.current.setZoom(16);
              }
            }}
            activeOpacity={0.8}
          >
            <Icon name="place" size={isMobile ? 20 : 24} color="white" />
          </TouchableOpacity>
        )}
      </View>

      {/* Map Legend (Admin Only) - hidden on mobile */}
      {isAdmin && !isMobile && (
        <View dataSet={{ uiFade: true }} style={[
          styles.legend,
          isMobile && styles.legendMobile,
          styles.uiElementTransition,
          { 
            backgroundColor: isDarkMode ? 'rgba(45, 45, 45, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            opacity: (uiFadeEnabled && isMapDragging) ? 0.3 : 1,
          }
        ]}>
        <Text style={[styles.legendTitle, isMobile && styles.legendTitleMobile, { color: colors.text }]}>Legend</Text>
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, { backgroundColor: '#94EB98' }]} />
          <Text style={[styles.legendText, { color: colors.textSecondary }]}>Players</Text>
        </View>
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, { backgroundColor: '#76AF87' }]} />
          <Text style={[styles.legendText, { color: colors.textSecondary }]}>Clubs</Text>
        </View>
        {/* Debug info - remove after testing */}
        {(() => {
          const counts = getVisibleMarkerCounts();
          return (
            <View style={{ marginTop: 8, borderTopWidth: 1, borderTopColor: '#ddd', paddingTop: 6 }}>
              <Text style={[styles.legendText, { color: colors.textSecondary, fontSize: 10 }]}>
                📍 Location: {location ? '✅' : '❌'}
              </Text>
              <Text style={[styles.legendText, { color: colors.textSecondary, fontSize: 10 }]}>
                👤 UserID: {currentUserDbId ? '✅' : '❌'}
              </Text>
              <Text style={[styles.legendText, { color: colors.textSecondary, fontSize: 10 }]}>
                🗺️ Users: {counts.visibleUsers}{counts.visibleUsers !== nearbyUsers.length ? `/${nearbyUsers.length}` : ''} | Hotspots: {counts.visibleHotspots}{counts.visibleHotspots !== hotspots.length ? `/${hotspots.length}` : ''}
              </Text>
            </View>
          );
        })()}
        </View>
      )}

      {showLocationOffBanner && (
        <View
          dataSet={{ uiFade: true }}
          style={[
            styles.locationOffBanner,
            styles.uiElementTransition,
            isMobile && styles.locationOffBannerMobile,
            {
              backgroundColor: colors.surface,
              borderColor: colors.border,
              opacity: (uiFadeEnabled && isMapDragging) ? 0.3 : 1,
            },
          ]}
        >
          <Text style={[styles.locationOffBannerText, { color: colors.text }]}>
            Location is off. Turn it on to see other players.
          </Text>
        </View>
      )}

      {!effectiveLocationEnabled && !showLocationOffBanner && (
        <View
          dataSet={{ uiFade: true }}
          style={[
            styles.locationOffHint,
            styles.uiElementTransition,
            isMobile && styles.locationOffHintMobile,
            {
              backgroundColor: colors.surface,
              borderColor: colors.border,
              opacity: (uiFadeEnabled && isMapDragging) ? 0.3 : 1,
            },
          ]}
        >
          <Text style={[styles.locationOffHintText, { color: colors.textSecondary }]}>
            Location off - turn on to see players
          </Text>
        </View>
      )}

      {/* Club: set marker prompt — shown when approved club hasn't set their map location yet */}
      {isApprovedClub && !clubMarkerLocation && !loading && (
        <TouchableOpacity
          dataSet={{ uiFade: true }}
          activeOpacity={0.85}
          style={[
            styles.clubSetMarkerBanner,
            styles.uiElementTransition,
            isMobile && styles.clubSetMarkerBannerMobile,
            {
              backgroundColor: colors.surface,
              borderColor: colors.primary || '#84D288',
              opacity: (uiFadeEnabled && isMapDragging) ? 0.3 : 1,
            },
          ]}
          onPress={() => setShowClubLocationPicker(true)}
        >
          <Icon name="place" size={20} color={colors.primary || '#84D288'} style={styles.clubSetMarkerIcon} />
          <View style={styles.clubSetMarkerTextWrap}>
            <Text style={[styles.clubSetMarkerTitle, { color: colors.text }]}>
              Your club isn't on the map yet
            </Text>
            <Text style={[styles.clubSetMarkerSubtext, { color: colors.textSecondary }]}>
              Tap here to set your location
            </Text>
          </View>
          <Icon name="chevron-right" size={22} color={colors.textSecondary} />
        </TouchableOpacity>
      )}

      {/* Location Sharing Toggle Button — hidden for approved clubs (they're always visible) */}
      {showLocationToggle && (
      <TouchableOpacity
        dataSet={{ uiFade: true }}
        style={[
          styles.locationToggleButton,
          styles.uiElementTransition,
          !locationEnabled && { backgroundColor: isDarkMode ? '#3D3D3D' : '#f0f0f0', borderColor: isDarkMode ? '#555' : '#ddd' },
          locationEnabled && styles.locationToggleButtonActive,
          isMobile && styles.locationToggleButtonMobile,
          isMobile && locationEnabled && styles.locationToggleButtonMobileActive,
          { opacity: (uiFadeEnabled && isMapDragging) ? 0.3 : 1 }
        ]}
        onPress={handleToggleLocation}
        activeOpacity={0.8}
      >
        <Icon
          name={locationEnabled ? 'location-on' : 'location-off'}
          size={isMobile ? 18 : 20}
          color={locationEnabled ? '#fff' : (isDarkMode ? '#aaa' : '#666')}
        />
        <Text
          numberOfLines={1}
          style={[
            styles.locationToggleText,
            isMobile && styles.locationToggleTextMobile,
            { color: locationEnabled ? '#fff' : (isDarkMode ? '#aaa' : '#666') },
          ]}
        >
          {locationEnabled ? 'On' : 'Off'}
        </Text>
      </TouchableOpacity>
      )}

      {/* Location visibility selector – Everyone / Friends Only (below location toggle) */}
      {showLocationToggle && locationEnabled && appUser?.accountType !== 'CLUB' && (
        <TouchableOpacity
          dataSet={{ uiFade: true }}
          style={[
            styles.visibilitySelector,
            styles.uiElementTransition,
            isMobile && styles.visibilitySelectorMobile,
            { opacity: (uiFadeEnabled && isMapDragging) ? 0.3 : 1 },
            locationVisibility === 'friends_only' && styles.visibilitySelectorActive,
          ]}
          onPress={handleToggleLocationVisibility}
          activeOpacity={0.8}
        >
          <Icon
            name={locationVisibility === 'friends_only' ? 'people' : 'public'}
            size={isMobile ? 16 : 18}
            color={locationVisibility === 'friends_only' ? '#fff' : (isDarkMode ? '#aaa' : '#666')}
          />
          <Text
            numberOfLines={1}
            style={[
              styles.visibilitySelectorText,
              isMobile && styles.visibilitySelectorTextMobile,
              { color: locationVisibility === 'friends_only' ? '#fff' : (isDarkMode ? '#aaa' : '#666') },
            ]}
          >
            {locationVisibility === 'friends_only' ? 'Fr' : 'All'}
          </Text>
        </TouchableOpacity>
      )}

      {/* Quick Pairing Button - Hide when modal is open */}
      {!showQuickPairSettings && !quickPairSearching && !quickPairMatchFound && (
        <View dataSet={{ uiFade: true }} style={[
          styles.quickPairingContainer,
          styles.uiElementTransition,
          { opacity: (uiFadeEnabled && isMapDragging) ? 0.3 : 1 }
        ]}>
          <TouchableOpacity
            style={[styles.quickPairingButton, isMobile && styles.quickPairingButtonMobile]}
            onPress={handleQuickPairPress}
            activeOpacity={0.8}
          >
            <Image 
              source={require('../../assets/quick-pairing-icon.png')} 
              style={[styles.quickPairingIcon, isMobile && styles.quickPairingIconMobile]}
              resizeMode="contain"
            />
            <Text style={[styles.quickPairingText, isMobile && styles.quickPairingTextMobile]}>Quick Pairing</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Quick Pair Settings Modal */}
      <QuickPairSettingsModal
        visible={showQuickPairSettings}
        onClose={() => setShowQuickPairSettings(false)}
        onStartSearch={handleStartQuickPairSearch}
      />

      {/* Quick Pair Search Overlay */}
      <QuickPairSearchOverlay
        visible={quickPairSearching || quickPairMatchFound}
        searching={quickPairSearching && !quickPairMatchFound}
        matchFound={quickPairMatchFound}
        matchedUser={matchedUser}
        matchDistance={matchDistance}
        matchPhase={matchPhase}
        timeRemaining={searchTimeRemaining}
        myRating={myRating}
        myProfilePictureUrl={myProfilePictureUrl}
        myUsername={appUser?.username}
        onSendMessage={handleQuickPairSendMessage}
        onCancel={handleQuickPairCancel}
      />

      {/* Game Result Popup */}
      {gameResult && (
        <GameResultPopup
          gameResult={gameResult}
          onDismiss={() => setGameResult(null)}
        />
      )}

      {/* Welcome to Pro Popup */}
      {showWelcomePro && (
        <WelcomeProPopup
          onDismiss={() => setShowWelcomePro(false)}
        />
      )}

      {/* Map Filter Modal */}
      <MapFilterModal
        visible={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        onApplyFilters={(filters) => {
          setActiveFilters(filters);
          showToast('Filters applied', 'success');
        }}
        initialFilters={activeFilters}
      />

      {/* Club Location Picker Modal */}
      <ClubLocationPickerModal
        visible={showClubLocationPicker}
        initialLocation={
          appUser?.latitude && appUser?.longitude
            ? { latitude: appUser.latitude, longitude: appUser.longitude }
            : null
        }
        deviceLocation={location}
        onConfirm={handleClubLocationConfirm}
        onDismiss={() => setShowClubLocationPicker(false)}
        clubName={appUser?.clubName}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  mapWrapper: {
    flex: 1,
    position: 'relative',
  },
  map: {
    width: '100%',
    height: '100%',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
  },
  loadingText: {
    fontSize: 18,
    color: '#666',
  },
  quickPairingContainer: {
    position: 'absolute',
    bottom: 20,/*note, please leave this value as is, otherwise the quick pairing button will be too far from the bottom of the screen*/ 
    width: "100%",
    alignSelf: 'center',
    alignItems: 'center',
    zIndex: 100, // Ensure button appears above checkerboard overlay
  },
  quickPairingButton: {
    backgroundColor: '#84D288',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 32,
    borderRadius: 10,
    maxWidth: 300,
    width: '90%',
    elevation: 8,
    shadowColor: '#76AF87',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 1,
    shadowRadius: 0,
  },
  quickPairingButtonMobile: {
    width: '85%',
    paddingVertical: 10,
    paddingHorizontal: 24,
  },
  quickPairingIcon: {
    width: 34,
    height: 34,
    marginRight: 12,
  },
  quickPairingIconMobile: {
    width: 28,
    height: 28,
    marginRight: 10,
  },
  quickPairingText: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
  },
  quickPairingTextMobile: {
    fontSize: 16,
  },
  topControlsRow: {
    position: 'absolute',
    top: 20,
    left: 20,
    right: 180,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    zIndex: 100,
  },
  topControlsRowMobile: {
    right: 56, // Smaller map controls on mobile
  },
  eventDiscoveryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#84D288',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 25,
    shadowColor: '#7FB690',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 0,
    elevation: 5,
    gap: 8,
  },
  eventDiscoveryButtonMobile: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 18,
    gap: 4,
  },
  eventDiscoveryButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: 'white',
  },
  eventDiscoveryButtonTextMobile: {
    fontSize: 12,
  },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#84D288',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 25,
    shadowColor: '#7FB690',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 0,
    elevation: 5,
    gap: 8,
    zIndex: 100,
  },
  filterButtonMobile: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 18,
    gap: 4,
  },
  filterButtonActive: {
    backgroundColor: '#76AF87',
  },
  filterButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: 'white',
  },
  filterButtonTextMobile: {
    fontSize: 12,
  },
  filterBadge: {
    backgroundColor: 'white',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    paddingHorizontal: 6,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 4,
  },
  filterBadgeText: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#84D288',
  },
  filterBadgeMobile: {
    minWidth: 16,
    height: 16,
    paddingHorizontal: 4,
  },
  filterBadgeTextMobile: {
    fontSize: 10,
  },
  layerToggle: {
    flexDirection: 'row',
    marginTop: 10,
    alignItems: 'center',
    borderRadius: 20,
    padding: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.15,
    shadowRadius: 3,
    elevation: 4,
  },
  layerToggleMobile: {
    padding: 2,
  },
  layerToggleButton: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 16,
  },
  layerToggleButtonMobile: {
    paddingHorizontal: 6,
    paddingVertical: 4,
    borderRadius: 12,
  },
  layerToggleButtonActive: {
    backgroundColor: '#84D288',
  },
  layerToggleText: {
    fontSize: 12,
    fontWeight: '600',
  },
  layerToggleTextMobile: {
    fontSize: 10,
  },
  layerToggleTextActive: {
    color: '#fff',
  },
  mapControls: {
    position: 'absolute',
    top: 20,
    right: 20,
    gap: 10,
    zIndex: 100,
  },
  mapControlsMobile: {
    top: 20,
    right: 12,
    gap: 6,
  },
  mapControlButton: {
    backgroundColor: '#84D288',
    borderRadius: 10,
    width: 48,
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#7FB690',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 0,
    elevation: 5,
  },
  mapControlButtonMobile: {
    width: 36,
    height: 36,
    borderRadius: 8,
  },
  locationButton: {
    position: 'absolute',
    top: 20,
    right: 20,
    backgroundColor: 'white',
    borderRadius: 30,
    width: 50,
    height: 50,
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    zIndex: 100, // Ensure button appears above checkerboard overlay
  },
  locationOffBanner: {
    position: 'absolute',
    top: 90,
    left: 20,
    right: 20,
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderWidth: 1,
    zIndex: 100,
    elevation: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
  },
  locationOffBannerMobile: {
    top: 80,
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  locationOffBannerText: {
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
  },
  locationOffHint: {
    position: 'absolute',
    top: 90,
    left: 20,
    right: 20,
    borderRadius: 10,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderWidth: 1,
    zIndex: 100,
    elevation: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 3,
  },
  locationOffHintMobile: {
    top: 80,
  },
  locationOffHintText: {
    fontSize: 12,
    fontWeight: '600',
    textAlign: 'center',
  },
  clubSetMarkerBanner: {
    position: 'absolute',
    top: 90,
    left: 20,
    right: 20,
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderWidth: 2,
    zIndex: 100,
    elevation: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    flexDirection: 'row',
    alignItems: 'center',
  },
  clubSetMarkerBannerMobile: {
    top: 80,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  clubSetMarkerIcon: {
    marginRight: 10,
  },
  clubSetMarkerTextWrap: {
    flex: 1,
  },
  clubSetMarkerTitle: {
    fontSize: 14,
    fontWeight: '600',
  },
  clubSetMarkerSubtext: {
    fontSize: 12,
    marginTop: 2,
    opacity: 0.9,
  },
  locationToggleButton: {
    position: 'absolute',
    top: 220,
    right: 20,
    backgroundColor: '#f0f0f0',
    borderRadius: 10,
    width: 48,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 3,
    elevation: 6,
    zIndex: 100,
    borderWidth: 2,
    borderColor: '#ddd',
  },
  locationToggleButtonMobile: {
    width: 36,
    height: 36,
    borderRadius: 8,
    gap: 0,
    top: 205,
  },
  locationToggleButtonActive: {
    backgroundColor: '#84D288',
    borderColor: '#7FB690',
    shadowColor: '#7FB690',
    shadowOpacity: 1,
    shadowRadius: 0,
  },
  locationToggleButtonMobileActive: {
    borderWidth: 0,
  },
  locationToggleText: {
    fontSize: 9,
    fontWeight: '700',
  },
  locationToggleTextMobile: {
    fontSize: 8,
  },
  visibilitySelector: {
    position: 'absolute',
    top: 275,
    right: 20,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    width: 48,
    height: 48,
    backgroundColor: '#f0f0f0',
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: '#ddd',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.15,
    shadowRadius: 2,
    elevation: 4,
    zIndex: 100,
    borderWidth: 1.5,
    borderColor: '#ddd',
  },
  visibilitySelectorActive: {
    backgroundColor: '#5C8DDB',
    borderColor: '#5C8DDB',
  },
  visibilitySelectorMobile: {
    top: 248,
    width: 36,
    height: 36,
    borderRadius: 8,
    gap: 0,
  },
  visibilitySelectorText: {
    fontSize: 9,
    fontWeight: '700',
  },
  visibilitySelectorTextMobile: {
    fontSize: 8,
  },
  legend: {
    position: 'absolute',
    bottom: 90,
    left: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 10,
    padding: 12,
    maxWidth: 160,
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    zIndex: 100,
  },
  legendMobile: {
    bottom: 80,
    left: 12,
    padding: 8,
    maxWidth: 110,
  },
  legendTitleMobile: {
    fontSize: 11,
    marginBottom: 4,
  },
  legendTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 4,
  },
  legendDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 8,
  },
  legendText: {
    fontSize: 12,
    color: '#666',
  },
  uiElementTransition: Platform.select({
    web: {
      transitionProperty: 'opacity',
      transitionDuration: '0.3s',
      transitionTimingFunction: 'ease',
    },
    default: {}, // Native doesn't need explicit transition
  }),
});

export default MapScreen;
